import React, { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import { usePreventScrollShift } from "./hooks/usePreventScrollShift";
import {
  TrendingDown,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Eye,
  Search,
  Filter,
  Calendar,
  DollarSign,
  FileText,
  User,
  Clock,
  ChevronDown,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Info,
  Users,
  Building2,
  Wallet,
  Package,
  Upload,
  Settings,
} from "lucide-react";

// ✅ CONFIGURACIÓN ANTI-ZOOM OBLIGATORIA
if (typeof document !== "undefined") {
  let metaViewport = document.querySelector('meta[name="viewport"]');
  if (!metaViewport) {
    metaViewport = document.createElement("meta");
    metaViewport.name = "viewport";
    document.head.appendChild(metaViewport);
  }
  metaViewport.content =
    "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover";
}

// ✅ COLORES DE VISTA COBROS
const getVistaColor = () => ({
  gradient: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
  solid: "#2563eb",
  light: "#2563eb10",
  description: "Pagos a trabajadores",
});

// Funciones helper para manejar fechas sin problemas de timezone
const formatDateForInput = (dateString) => {
  if (!dateString) return new Date().toISOString().split("T")[0];
  if (dateString.includes("T")) {
    return dateString.split("T")[0];
  }
  return dateString;
};

const formatDateForDisplay = (dateString) => {
  if (!dateString) return "";

  // Tomar directamente los componentes del string YYYY-MM-DD
  const [year, month, day] = dateString.split("T")[0].split("-");
  return `${day}/${month}/${year}`;
};

// ✅ FUNCIÓN PARA FORMATEAR CUIT EN VIVO (guiones al instante)
const formatearCUIT = (cuit) => {
  if (!cuit) return "";
  const numeros = cuit.replace(/\D/g, "").slice(0, 11);
  if (numeros.length <= 2) return numeros;
  if (numeros.length <= 10) return `${numeros.slice(0, 2)}-${numeros.slice(2)}`;
  return `${numeros.slice(0, 2)}-${numeros.slice(2, 10)}-${numeros.slice(10)}`;
};

// Dropdown personalizado unificado con búsqueda
const CustomDropdown = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  icon: Icon,
  onSearch,
  canCreate = false,
  onCreateNew,
  createText = "Crear nuevo",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const selected = options.find((opt) => opt.value === value);
    setSelectedOption(selected);
  }, [value, options]);

  useEffect(() => {
    const filtered = options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOptions(filtered);
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  return (
    <div
      ref={dropdownRef}
      style={{ position: "relative", width: "100%", maxWidth: "100%" }}
    >
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: "100%",
          padding: "14px 16px",
          border: "2px solid #f1f5f9",
          borderRadius: "12px",
          background: disabled ? "#f8fafc" : "white",
          color: disabled ? "#94a3b8" : "#1e293b",
          fontSize: "16px",
          fontWeight: "500",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "not-allowed" : "pointer",
          outline: "none",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          minHeight: "44px",
          touchAction: "manipulation",
          boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
          boxSizing: "border-box",
        }}
        onFocus={(e) => {
          e.target.style.borderColor = "#3b82f6";
          e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
        }}
        onBlur={(e) => {
          e.target.style.borderColor = "#f1f5f9";
          e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flex: 1,
            minWidth: 0,
          }}
        >
          {Icon && <Icon size={16} color="#64748b" />}
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronDown
          size={16}
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            flexShrink: 0,
            color: "#64748b",
          }}
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            background: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
            marginTop: "4px",
            maxHeight: "280px",
            overflowY: "auto",
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
          }}
          onWheel={(e) => {
            // Evita que el scroll burbujee al body cuando se llega al límite
            e.stopPropagation();
          }}
          onTouchMove={(e) => {
            // En mobile, evita scroll-bounce hacia el body
            e.stopPropagation();
          }}
        >
          {/* Campo de búsqueda */}
          <div style={{ padding: "8px" }}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                fontSize: "14px",
                outline: "none",
                boxSizing: "border-box",
                touchAction: "manipulation",
              }}
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  canCreate &&
                  filteredOptions.length === 0 &&
                  searchTerm.trim()
                ) {
                  onCreateNew?.(searchTerm.trim());
                  setSearchTerm("");
                  setIsOpen(false);
                }
              }}
            />
          </div>

          {/* Opciones filtradas */}
          {filteredOptions.map((option, index) => (
            <button
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
                setSearchTerm("");
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "none",
                background: option.value === value ? "#eff6ff" : "transparent",
                color: option.value === value ? "#3b82f6" : "#374151",
                fontSize: "16px",
                fontWeight: option.value === value ? "600" : "400",
                textAlign: "left",
                cursor: "pointer",
                transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={(e) => {
                if (option.value !== value) {
                  e.target.style.background = "#f8fafc";
                }
              }}
              onMouseLeave={(e) => {
                if (option.value !== value) {
                  e.target.style.background = "transparent";
                }
              }}
            >
              {option.label}
            </button>
          ))}

          {/* Opción de crear nuevo */}
          {canCreate && searchTerm.trim() && filteredOptions.length === 0 && (
            <button
              onClick={() => {
                onCreateNew?.(searchTerm.trim());
                setSearchTerm("");
                setIsOpen(false);
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "none",
                background: "#f0fdf4",
                color: "#166534",
                fontSize: "14px",
                fontWeight: "600",
                textAlign: "left",
                cursor: "pointer",
                borderTop: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                touchAction: "manipulation",
              }}
            >
              <Plus size={14} />
              {createText}: "{searchTerm}"
            </button>
          )}

          {/* Mensaje cuando no hay resultados */}
          {!canCreate && filteredOptions.length === 0 && searchTerm && (
            <div
              style={{
                padding: "16px",
                textAlign: "center",
                color: "#64748b",
                fontSize: "14px",
              }}
            >
              No se encontraron resultados
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Modal personalizado para alertas/confirmaciones
function CustomModal({
  isOpen,
  onClose,
  type = "info",
  title,
  message,
  onConfirm,
  confirmText = "Aceptar",
  cancelText = "Cancelar",
  isMobile,
}) {
  if (!isOpen) return null;

  const getColors = () => {
    switch (type) {
      case "confirm":
        return {
          bg: "#fef3cd",
          border: "#f59e0b",
          text: "#92400e",
          button: "#f59e0b",
          icon: AlertCircle,
        };
      case "error":
        return {
          bg: "#fef2f2",
          border: "#ef4444",
          text: "#dc2626",
          button: "#ef4444",
          icon: XCircle,
        };
      case "success":
        return {
          bg: "#f0fdf4",
          border: "#22c55e",
          text: "#166534",
          button: "#22c55e",
          icon: CheckCircle2,
        };
      default:
        return {
          bg: "#f0f9ff",
          border: "#3b82f6",
          text: "#1d4ed8",
          button: "#3b82f6",
          icon: Info,
        };
    }
  };

  const colors = getColors();
  const IconComponent = colors.icon;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 15000,
        padding: "20px",
        animation: "fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          padding: "24px",
          width: "100%",
          maxWidth: isMobile ? "90%" : "400px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          border: `2px solid ${colors.border}`,
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            textAlign: "center",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              marginBottom: "12px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <IconComponent size={32} color={colors.text} />
          </div>
          <h3
            style={{
              margin: "0 0 8px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: colors.text,
            }}
          >
            {title}
          </h3>
          <p
            style={{
              margin: 0,
              fontSize: "14px",
              color: "#64748b",
              lineHeight: "1.4",
            }}
          >
            {message}
          </p>
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: type === "confirm" ? "space-between" : "center",
          }}
        >
          {type === "confirm" && (
            <button
              onClick={onClose}
              className="button-hover"
              style={{
                flex: 1,
                padding: "12px 20px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                background: "white",
                color: "#64748b",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
              }}
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={() => {
              if (type === "confirm" && onConfirm) {
                onConfirm();
              }
              onClose();
            }}
            className="button-hover"
            style={{
              flex: type === "confirm" ? 1 : "auto",
              padding: "12px 20px",
              border: "none",
              borderRadius: "8px",
              background: colors.button,
              color: "white",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              minWidth: type !== "confirm" ? "120px" : "auto",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal para vista completa del cobro
function CobroDetailModal({
  cobro,
  trabajadores,
  conceptos,
  cajas,
  constructora,
  onClose,
  isMobile,
}) {
  const trabajador = trabajadores.find((t) => t.id === cobro.trabajador_id);
  const concepto = conceptos.find((c) => c.id === cobro.concepto_id);
  const cajaOrigen = cajas.find((c) => c.id === cobro.caja_origen_id);
  const colorVista = getVistaColor();

  const obtenerNombrePorCuit = (cuit) => {
    if (!cuit) return "";

    // Buscar en constructora
    if (constructora?.cuit === cuit) {
      return constructora.nombre;
    }

    // Buscar en trabajadores
    const trabajadorEncontrado = trabajadores.find((t) => t.cuit === cuit);
    if (trabajadorEncontrado) {
      return `${trabajadorEncontrado.apellido}, ${trabajadorEncontrado.nombre}`;
    }

    return "";
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: isMobile ? "10px 10px 80px 10px" : "20px",
        animation: "fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          width: "100%",
          maxWidth: isMobile ? "100%" : "800px",
          maxHeight: isMobile ? "calc(100vh - 160px)" : "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 80px rgba(0,0,0,0.3)",
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: colorVista.gradient,
            color: "white",
            padding: isMobile ? "20px" : "25px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: 1,
              minWidth: 0,
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                background: "rgba(255,255,255,0.2)",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Eye size={20} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2
                style={{
                  margin: "0 0 4px 0",
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Vista Detallada - Cobro
              </h2>
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  opacity: 0.9,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {trabajador?.apellido}, {trabajador?.nombre} -{" "}
                {formatDateForDisplay(cobro.fecha)}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="button-hover"
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "10px",
              padding: "8px 12px",
              color: "white",
              fontSize: "16px",
              cursor: "pointer",
              fontWeight: "600",
              flexShrink: 0,
              minWidth: "max-content",
              whiteSpace: "nowrap",
              touchAction: "manipulation",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Contenido */}
        <div
          style={{
            flex: 1,
            padding: isMobile ? "20px" : "30px",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: "24px",
            }}
          >
            {/* Información Principal */}
            <div>
              <h3
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Users size={20} color={colorVista.solid} />
                Información Principal
              </h3>

              <div style={{ display: "grid", gap: "16px" }}>
                {/* Trabajador */}
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "2px solid #e2e8f0",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                    }}
                  >
                    Trabajador
                  </p>
                  <p
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#1e293b",
                    }}
                  >
                    {trabajador?.apellido}, {trabajador?.nombre}
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      color: "#64748b",
                    }}
                  >
                    {trabajador?.puesto} ({trabajador?.tipo})
                  </p>
                  {trabajador?.cuit && (
                    <p
                      style={{
                        margin: "4px 0 0 0",
                        fontSize: "13px",
                        color: "#64748b",
                        fontFamily: "monospace",
                      }}
                    >
                      CUIT: {trabajador.cuit}
                    </p>
                  )}
                </div>

                {/* Concepto */}
                <div
                  style={{
                    background: `${colorVista.solid}10`,
                    padding: "16px",
                    borderRadius: "12px",
                    border: `2px solid ${colorVista.solid}20`,
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: colorVista.solid,
                      textTransform: "uppercase",
                    }}
                  >
                    Concepto
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "700",
                      color: colorVista.solid,
                    }}
                  >
                    {concepto?.nombre}
                  </p>
                  {cobro.generado_desde_entrada && (
                    <div
                      style={{
                        margin: "4px 0 0 0",
                        fontSize: "12px",
                        color: "#f59e0b",
                        fontWeight: "500",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <DollarSign size={12} />
                      Generado automáticamente desde entrada
                    </div>
                  )}
                </div>

                {/* Monto */}
                <div
                  style={{
                    background: colorVista.gradient,
                    padding: "20px",
                    borderRadius: "12px",
                    border: `2px solid ${colorVista.solid}`,
                    textAlign: "center",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "white",
                      textTransform: "uppercase",
                      opacity: 0.9,
                    }}
                  >
                    Monto
                  </p>
                  <p
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: isMobile ? "24px" : "28px",
                      fontWeight: "800",
                      color: "white",
                    }}
                  >
                    {cobro.moneda} ${Number(cobro.monto).toLocaleString()}
                  </p>
                  {cobro.tipo_cambio && cobro.tipo_cambio !== "1" && (
                    <p
                      style={{
                        margin: 0,
                        fontSize: "14px",
                        color: "white",
                        opacity: 0.9,
                      }}
                    >
                      Tipo de cambio: ${cobro.tipo_cambio}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Información Adicional */}
            <div>
              <h3
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <FileText size={20} color={colorVista.solid} />
                Detalles Adicionales
              </h3>

              <div style={{ display: "grid", gap: "16px" }}>
                {/* Fecha */}
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "2px solid #e2e8f0",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                    }}
                  >
                    Fecha
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#1e293b",
                    }}
                  >
                    {formatDateForDisplay(cobro.fecha)}
                  </p>
                </div>

                {/* Caja de Origen */}
                <div
                  style={{
                    background: "#eff6ff",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "2px solid #dbeafe",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#1e40af",
                      textTransform: "uppercase",
                    }}
                  >
                    Caja de Origen
                  </p>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Wallet size={16} color="#1d4ed8" />
                    <span
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#1d4ed8",
                      }}
                    >
                      {cajaOrigen?.nombre} {cajaOrigen?.moneda}
                    </span>
                  </div>
                </div>

                {/* Método de pago */}
                {cobro.metodo_pago && (
                  <div
                    style={{
                      background: "#f8fafc",
                      padding: "16px",
                      borderRadius: "12px",
                      border: "2px solid #e2e8f0",
                    }}
                  >
                    <p
                      style={{
                        margin: "0 0 6px 0",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                      }}
                    >
                      Método de Pago
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#1e293b",
                        textTransform: "capitalize",
                      }}
                    >
                      {cobro.metodo_pago}
                    </p>
                  </div>
                )}

                {/* Descripción */}
                {cobro.descripcion && (
                  <div
                    style={{
                      background: "#f8fafc",
                      padding: "16px",
                      borderRadius: "12px",
                      border: "2px solid #e2e8f0",
                    }}
                  >
                    <p
                      style={{
                        margin: "0 0 6px 0",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                      }}
                    >
                      Descripción
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "14px",
                        color: "#1e293b",
                        lineHeight: "1.5",
                      }}
                    >
                      {cobro.descripcion}
                    </p>
                  </div>
                )}

                {/* Proyecto relacionado si es generado desde entrada */}
                {cobro.generado_desde_entrada && cobro.entradas?.proyectos && (
                  <div
                    style={{
                      background: "#fefbf3",
                      padding: "16px",
                      borderRadius: "12px",
                      border: "2px solid #fbbf24",
                    }}
                  >
                    <p
                      style={{
                        margin: "0 0 6px 0",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#92400e",
                        textTransform: "uppercase",
                      }}
                    >
                      Proyecto Relacionado
                    </p>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <Building2 size={16} color="#f59e0b" />
                      <span
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          color: "#f59e0b",
                        }}
                      >
                        {cobro.entradas.proyectos.codigo}
                      </span>
                    </div>
                  </div>
                )}

                {/* Datos de facturación */}
                {cobro.tiene_factura &&
                  (cobro.facturador || cobro.factura_a_nombre_de) && (
                    <div
                      style={{
                        background: "#fefbf3",
                        padding: "16px",
                        borderRadius: "12px",
                        border: "2px solid #fbbf24",
                      }}
                    >
                      <p
                        style={{
                          margin: "0 0 12px 0",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#f59e0b",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <FileText size={16} />
                        Datos de Facturación
                      </p>

                      {cobro.facturador && (
                        <div style={{ marginBottom: "12px" }}>
                          <p
                            style={{
                              margin: "0 0 4px 0",
                              fontSize: "11px",
                              fontWeight: "600",
                              color: "#92400e",
                              textTransform: "uppercase",
                            }}
                          >
                            Facturador
                          </p>
                          <p
                            style={{
                              margin: "0 0 2px 0",
                              fontSize: "14px",
                              fontWeight: "600",
                              color: "#1e293b",
                              fontFamily: "monospace",
                            }}
                          >
                            {cobro.facturador}
                          </p>
                          {obtenerNombrePorCuit(cobro.facturador) && (
                            <div
                              style={{
                                margin: 0,
                                fontSize: "12px",
                                color: "#059669",
                                fontWeight: "500",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <User size={12} />
                              {obtenerNombrePorCuit(cobro.facturador)}
                            </div>
                          )}
                        </div>
                      )}

                      {cobro.factura_a_nombre_de && (
                        <div>
                          <p
                            style={{
                              margin: "0 0 4px 0",
                              fontSize: "11px",
                              fontWeight: "600",
                              color: "#92400e",
                              textTransform: "uppercase",
                            }}
                          >
                            Factura a nombre de
                          </p>
                          <p
                            style={{
                              margin: "0 0 2px 0",
                              fontSize: "14px",
                              fontWeight: "600",
                              color: "#1e293b",
                              fontFamily: "monospace",
                            }}
                          >
                            {cobro.factura_a_nombre_de}
                          </p>
                          {obtenerNombrePorCuit(cobro.factura_a_nombre_de) && (
                            <div
                              style={{
                                margin: 0,
                                fontSize: "12px",
                                color: "#059669",
                                fontWeight: "500",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <User size={12} />
                              {obtenerNombrePorCuit(cobro.factura_a_nombre_de)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
              </div>
            </div>
          </div>

          {/* Documentos adjuntos */}
          {(cobro.tiene_comprobante || cobro.tiene_factura) && (
            <div
              style={{
                marginTop: "32px",
              }}
            >
              <h3
                style={{
                  margin: "0 0 20px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <FileText size={20} color={colorVista.solid} />
                Documentos Adjuntos
              </h3>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "1fr"
                    : "repeat(auto-fit, minmax(300px, 1fr))",
                  gap: "20px",
                }}
              >
                {cobro.tiene_comprobante && cobro.comprobante_url && (
                  <div
                    style={{
                      background: "white",
                      border: "2px solid #22c55e",
                      borderRadius: "12px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        background:
                          "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                        color: "white",
                        padding: "12px 16px",
                        fontSize: "14px",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <FileText size={16} />
                        <span>Comprobante</span>
                      </div>
                      <div style={{ display: "flex", gap: "4px" }}>
                        <button
                          onClick={() => {
                            const fileName = `comprobante_${
                              trabajador?.apellido
                            }_${formatDateForDisplay(cobro.fecha).replace(
                              /\//g,
                              "-"
                            )}.${cobro.comprobante_url.split(".").pop()}`;

                            const a = document.createElement("a");
                            a.href = cobro.comprobante_url;
                            a.download = fileName;
                            a.target = "_blank";
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                          }}
                          className="button-hover"
                          style={{
                            background: "rgba(255,255,255,0.2)",
                            border: "none",
                            borderRadius: "4px",
                            padding: "4px 6px",
                            color: "white",
                            fontSize: "10px",
                            cursor: "pointer",
                            fontWeight: "600",
                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                            touchAction: "manipulation",
                          }}
                        >
                          💾
                        </button>
                        <button
                          onClick={() =>
                            window.open(cobro.comprobante_url, "_blank")
                          }
                          className="button-hover"
                          style={{
                            background: "rgba(255,255,255,0.2)",
                            border: "none",
                            borderRadius: "4px",
                            padding: "4px 6px",
                            color: "white",
                            fontSize: "10px",
                            cursor: "pointer",
                            fontWeight: "600",
                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                            touchAction: "manipulation",
                          }}
                        >
                          <Eye size={10} />
                        </button>
                      </div>
                    </div>
                    <div
                      style={{
                        height: "200px",
                        background: "#000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        window.open(cobro.comprobante_url, "_blank")
                      }
                    >
                      {cobro.comprobante_url.match(
                        /\.(jpg|jpeg|png|gif|webp)$/i
                      ) ? (
                        <img
                          src={cobro.comprobante_url}
                          alt="Comprobante"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            color: "white",
                            textAlign: "center",
                          }}
                        >
                          <FileText
                            size={32}
                            color="white"
                            style={{ marginBottom: "8px" }}
                          />
                          <p style={{ fontSize: "12px", margin: 0 }}>
                            Vista previa no disponible
                          </p>
                          <p
                            style={{
                              fontSize: "10px",
                              margin: "4px 0 0 0",
                              opacity: 0.8,
                            }}
                          >
                            Click para abrir
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {cobro.tiene_factura && cobro.factura_url && (
                  <div
                    style={{
                      background: "white",
                      border: "2px solid #f59e0b",
                      borderRadius: "12px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        background:
                          "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                        color: "white",
                        padding: "12px 16px",
                        fontSize: "14px",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <FileText size={16} />
                        <span>Factura</span>
                      </div>
                      <div style={{ display: "flex", gap: "4px" }}>
                        <button
                          onClick={() => {
                            const fileName = `factura_${
                              trabajador?.apellido
                            }_${formatDateForDisplay(cobro.fecha).replace(
                              /\//g,
                              "-"
                            )}.${cobro.factura_url.split(".").pop()}`;

                            const a = document.createElement("a");
                            a.href = cobro.factura_url;
                            a.download = fileName;
                            a.target = "_blank";
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                          }}
                          className="button-hover"
                          style={{
                            background: "rgba(255,255,255,0.2)",
                            border: "none",
                            borderRadius: "4px",
                            padding: "4px 6px",
                            color: "white",
                            fontSize: "10px",
                            cursor: "pointer",
                            fontWeight: "600",
                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                            touchAction: "manipulation",
                          }}
                        >
                          💾
                        </button>
                        <button
                          onClick={() =>
                            window.open(cobro.factura_url, "_blank")
                          }
                          className="button-hover"
                          style={{
                            background: "rgba(255,255,255,0.2)",
                            border: "none",
                            borderRadius: "4px",
                            padding: "4px 6px",
                            color: "white",
                            fontSize: "10px",
                            cursor: "pointer",
                            fontWeight: "600",
                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                            touchAction: "manipulation",
                          }}
                        >
                          <Eye size={10} />
                        </button>
                      </div>
                    </div>
                    <div
                      style={{
                        height: "200px",
                        background: "#000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                      onClick={() => window.open(cobro.factura_url, "_blank")}
                    >
                      {cobro.factura_url.match(
                        /\.(jpg|jpeg|png|gif|webp)$/i
                      ) ? (
                        <img
                          src={cobro.factura_url}
                          alt="Factura"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            color: "white",
                            textAlign: "center",
                          }}
                        >
                          <FileText
                            size={32}
                            color="white"
                            style={{ marginBottom: "8px" }}
                          />
                          <p style={{ fontSize: "12px", margin: 0 }}>
                            Vista previa no disponible
                          </p>
                          <p
                            style={{
                              fontSize: "10px",
                              margin: "4px 0 0 0",
                              opacity: 0.8,
                            }}
                          >
                            Click para abrir
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Modal simple para visualizar documento individual
function DocumentViewModal({ url, tipo, fileName, onClose, isMobile }) {
  const handleDownload = async () => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = fileName || `${tipo}_${Date.now()}.${url.split(".").pop()}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error descargando archivo:", error);
    }
  };

  const isImage = url.match(/\.(jpg|jpeg|png|gif|webp)$/i);
  const isPDF = url.match(/\.pdf$/i);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.9)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2500,
        padding: "10px",
        animation: "fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "12px",
          width: "100%",
          maxWidth: isMobile ? "100%" : "90vw",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header simple */}
        <div
          style={{
            background: tipo === "factura" ? "#f59e0b" : "#22c55e",
            color: "white",
            padding: "16px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <FileText size={16} />
            <h3
              style={{
                margin: 0,
                fontSize: "16px",
                fontWeight: "600",
              }}
            >
              {tipo === "factura" ? "Factura" : "Comprobante"}
            </h3>
          </div>

          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={handleDownload}
              className="button-hover"
              style={{
                background: "rgba(255,255,255,0.2)",
                border: "none",
                borderRadius: "6px",
                padding: "8px 12px",
                color: "white",
                fontSize: "14px",
                cursor: "pointer",
                fontWeight: "600",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              💾 Descargar
            </button>
            <button
              onClick={onClose}
              className="button-hover"
              style={{
                background: "rgba(255,255,255,0.2)",
                border: "none",
                borderRadius: "6px",
                padding: "8px 12px",
                color: "white",
                fontSize: "14px",
                cursor: "pointer",
                fontWeight: "600",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
              }}
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Contenido del documento */}
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#000",
            minHeight: isMobile ? "400px" : "500px",
          }}
        >
          {isImage ? (
            <img
              src={url}
              alt={tipo}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          ) : isPDF ? (
            <iframe
              src={url}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                minHeight: "500px",
              }}
              title={tipo}
            />
          ) : (
            <div
              style={{
                color: "white",
                textAlign: "center",
                padding: "40px",
              }}
            >
              <FileText
                size={64}
                color="white"
                style={{ marginBottom: "16px" }}
              />
              <p style={{ fontSize: "16px", marginBottom: "20px" }}>
                Archivo no previsualizable
              </p>
              <button
                onClick={handleDownload}
                className="button-hover"
                style={{
                  background: tipo === "factura" ? "#f59e0b" : "#22c55e",
                  color: "white",
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                }}
              >
                Descargar archivo
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Cobros({ constructora, usuario }) {
  const [cobros, setCobros] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [trabajadores, setTrabajadores] = useState([]);
  const [conceptos, setConceptos] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showModal, setShowModal] = useState(false);
  const [editingCobro, setEditingCobro] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterTrabajador, setFilterTrabajador] = useState("todos");
  const [filterConcepto, setFilterConcepto] = useState("todos");
  const [filterMonth, setFilterMonth] = useState("todos");
  const [filterEstado, setFilterEstado] = useState("activos");
  
  // Estado para el panel de filtros
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const isCliente = usuario?.tipo_usuario === "cliente";
  const permisosCliente =
    isCliente && usuario?.permisos ? usuario.permisos : null;

  // Estados para modales
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCobroDetail, setSelectedCobroDetail] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState(null);

  // Estados para alertas personalizadas
  const [customModal, setCustomModal] = useState({
    isOpen: false,
    type: "info",
    title: "",
    message: "",
    onConfirm: null,
  });

  const colorVista = getVistaColor();

  // ✅ SCROLL RESET OBLIGATORIO al cambiar vista
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // ✅ ESCAPE handler obligatorio para cerrar modales
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setShowModal(false);
        setShowDetailModal(false);
        setShowDocumentModal(false);
        setCustomModal((prev) => ({ ...prev, isOpen: false }));
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (constructora?.id) {
      cargarDatos();
    }
  }, [constructora, filterEstado]);

  const showCustomAlert = (type, title, message, onConfirm = null) => {
    setCustomModal({
      isOpen: true,
      type,
      title,
      message,
      onConfirm,
    });
  };

  // Función para abrir vista detallada
  const openDetailModal = (cobro) => {
    setSelectedCobroDetail(cobro);
    setShowDetailModal(true);
  };

  // Función para abrir documento individual
  const openDocumentModal = (url, tipo, cobro) => {
    const trabajador = trabajadores.find((t) => t.id === cobro.trabajador_id);
    const fileName = `${tipo}_${
      trabajador?.apellido || "cobro"
    }_${formatDateForDisplay(cobro.fecha).replace(/\//g, "-")}.${url
      .split(".")
      .pop()}`;

    setSelectedDocument(url);
    setSelectedDocumentType(tipo);
    setShowDocumentModal(true);
  };

  const cargarDatos = async () => {
    try {
      // ✅ CARGAR COBROS CON DATOS RELACIONADOS INCLUYENDO ENTRADAS Y COMISIONES
      let query = supabase
        .from("cobros_trabajadores")
        .select(
          `
          *,
          trabajadores (nombre, apellido, puesto, tipo, cuit),
          conceptos_cobro (nombre),
          cajas (nombre, tipo_caja, moneda),
          entradas!entrada_relacionada_id (id, proyecto_id, proyectos(codigo, apellido_cliente)),
          comisiones!comision_relacionada_id (
            id,
            monto_comision,
            moneda,
            salidas (
              proveedores_servicios (nombre),
              proyectos (codigo, apellido_cliente)
            )
          )
        `
        )
        .eq("constructora_id", constructora.id);

      if (filterEstado === "activos") {
        query = query.eq("activo", true).is("deleted_at", null);
      } else if (filterEstado === "eliminados") {
        query = query.or("activo.eq.false,deleted_at.not.is.null");
      }

      const { data: cobrosData, error: cobrosError } = await query.order(
        "fecha",
        { ascending: false }
      );

      if (cobrosError) {
        console.error("Error cargando cobros:", cobrosError);
      } else {
        setCobros(cobrosData || []);
      }

      // ✅ CARGAR TRABAJADORES ACTIVOS CON CUIT
      const { data: trabajadoresData, error: trabajadoresError } =
        await supabase
          .from("trabajadores")
          .select("id, nombre, apellido, puesto, tipo, cuit, activo")
          .eq("constructora_id", constructora.id)
          .eq("activo", true)
          .order("apellido");

      if (trabajadoresError) {
        console.error("Error cargando trabajadores:", trabajadoresError);
      } else {
        let lista = trabajadoresData || [];
        // Incluir trabajadores inactivos que estén referenciados por cobros
        const idsReferenciados = Array.from(
          new Set(
            (cobrosData || []).map((c) => c.trabajador_id).filter(Boolean)
          )
        );
        const idsFaltantes = idsReferenciados.filter(
          (id) => !lista.some((t) => t.id === id)
        );
        if (idsFaltantes.length > 0) {
          const { data: extra, error: extraError } = await supabase
            .from("trabajadores")
            .select("id, nombre, apellido, puesto, tipo, cuit, activo")
            .in("id", idsFaltantes);
          if (!extraError && extra && extra.length > 0) {
            lista = [...lista, ...extra];
          }
        }
        setTrabajadores(lista);
      }

      // Cargar conceptos de cobro
      const { data: conceptosData, error: conceptosError } = await supabase
        .from("conceptos_cobro")
        .select("*")
        .eq("constructora_id", constructora.id)
        .eq("activo", true)
        .order("nombre");

      if (conceptosError) {
        console.error("Error cargando conceptos:", conceptosError);
      } else {
        setConceptos(conceptosData || []);
      }

      // Cargar cajas de empresa (solo empresa, no por proyecto)
      const { data: cajasData, error: cajasError } = await supabase
        .from("cajas")
        .select("*")
        .eq("constructora_id", constructora.id)
        .eq("tipo_caja", "empresa")
        .order("moneda");

      if (cajasError) {
        console.error("Error cargando cajas:", cajasError);
      } else {
        setCajas(cajasData || []);
      }

      // Cargar proyectos según permisos
      let proyectosData = [];
      let proyectosError = null;

      if (filterEstado === "eliminados") {
        // Detectar proyecto_id desde entradas vinculadas a cobros
        const { data: idsData, error: idsError } = await supabase
          .from("cobros_trabajadores")
          .select("entrada_relacionada_id, entrada_id")
          .eq("constructora_id", constructora.id)
          .or("activo.eq.false,deleted_at.not.is.null");

        if (idsError) {
          proyectosError = idsError;
        } else {
          const entradaIds = Array.from(
            new Set(
              (idsData || [])
                .map((r) => r.entrada_relacionada_id || r.entrada_id)
                .filter(Boolean)
            )
          );
          if (entradaIds.length > 0) {
            const { data: entradasRows, error: entErr } = await supabase
              .from("entradas")
              .select("proyecto_id")
              .in("id", entradaIds);
            if (entErr) {
              proyectosError = entErr;
            } else {
              const projIds = Array.from(
                new Set(
                  (entradasRows || []).map((r) => r.proyecto_id).filter(Boolean)
                )
              );
              if (projIds.length > 0) {
                let query = supabase
                  .from("proyectos")
                  .select("*")
                  .eq("constructora_id", constructora.id)
                  .in("id", projIds)
                  .order("codigo");
                if (isCliente && permisosCliente?.proyectoId) {
                  if (projIds.includes(permisosCliente.proyectoId)) {
                    query = query.eq("id", permisosCliente.proyectoId);
                  } else {
                    proyectosData = [];
                    query = null;
                  }
                }
                if (query) {
                  const { data, error } = await query;
                  proyectosData = data || [];
                  proyectosError = error || null;
                }
              } else {
                proyectosData = [];
              }
            }
          } else {
            proyectosData = [];
          }
        }
      } else {
        let query = supabase
          .from("proyectos")
          .select("*")
          .eq("constructora_id", constructora.id)
          .eq("activo", true)
          .is("deleted_at", null);

        if (isCliente && permisosCliente?.proyectoId) {
          query = query.eq("id", permisosCliente.proyectoId);
        } else {
          query = query.order("codigo");
        }

        const { data, error } = await query;
        proyectosData = data || [];
        proyectosError = error || null;
      }

      if (proyectosError) {
        console.error("Error cargando proyectos:", proyectosError);
      } else {
        setProyectos(proyectosData || []);
      }
    } catch (err) {
      console.error("Error:", err);
    }
    setLoading(false);
  };

  // Función para detectar si hay filtros activos
  const hayFiltrosActivos = () => {
    return (
      filterTrabajador !== "todos" ||
      filterConcepto !== "todos" ||
      filterMonth !== "todos" ||
      filterEstado !== "activos"
    );
  };

  // Filtrar cobros
  const filteredCobros = cobros.filter((cobro) => {
    const nombreCompleto =
      `${cobro.trabajadores?.nombre} ${cobro.trabajadores?.apellido}`.toLowerCase();
    const matchesSearch =
      nombreCompleto.includes(searchTerm.toLowerCase()) ||
      cobro.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cobro.conceptos_cobro?.nombre
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesTrabajador =
      filterTrabajador === "todos" || cobro.trabajador_id === filterTrabajador;
    const matchesConcepto =
      filterConcepto === "todos" || cobro.concepto_id === filterConcepto;
    const matchesMonth =
      filterMonth === "todos" || cobro.fecha.substring(0, 7) === filterMonth;

    return (
      matchesSearch && matchesTrabajador && matchesConcepto && matchesMonth
    );
  });

  const handleEliminar = async (id, tieneEntradaRelacionada) => {
    const mensaje = tieneEntradaRelacionada
      ? "Este cobro tiene una entrada asociada. Se eliminarán ambos y se revertirá el impacto en cajas. ¿Continuar?"
      : "Se revertirá el impacto en cajas y el cobro quedará eliminado (recuperable). ¿Continuar?";

    showCustomAlert("confirm", "¿Eliminar cobro?", mensaje, async () => {
      try {
        const { error } = await supabase.rpc(
          "rpc_soft_delete_cobro_trabajador",
          {
            p_cobro_id: id,
            p_usuario_id: usuario.id,
            p_razon: null,
          }
        );
        if (error) {
          showCustomAlert(
            "error",
            "Error",
            "Error al eliminar cobro: " + error.message
          );
        } else {
          await cargarDatos();
          showCustomAlert("success", "Éxito", "Cobro eliminado correctamente");
        }
      } catch (err) {
        showCustomAlert("error", "Error", "Error: " + err.message);
      }
    });
  };

  const handleRestaurar = async (id, tieneEntradaRelacionada) => {
    const mensaje = tieneEntradaRelacionada
      ? "Este cobro tiene una entrada asociada. Se restaurarán ambos y se re-aplicará el impacto en cajas. ¿Continuar?"
      : "Se re-aplicará el impacto en cajas y el cobro quedará restaurado. ¿Continuar?";

    showCustomAlert("confirm", "¿Restaurar cobro?", mensaje, async () => {
      try {
        const { error } = await supabase.rpc("rpc_restore_cobro_trabajador", {
          p_cobro_id: id,
          p_usuario_id: usuario.id,
        });
        if (error) {
          showCustomAlert(
            "error",
            "Error",
            "Error al restaurar: " + error.message
          );
        } else {
          await cargarDatos();
          showCustomAlert("success", "Éxito", "Cobro restaurado correctamente");
        }
      } catch (err) {
        showCustomAlert("error", "Error", "Error: " + err.message);
      }
    });
  };

  // Obtener meses únicos para el filtro
  const mesesUnicos = [...new Set(cobros.map((c) => c.fecha.substring(0, 7)))]
    .sort()
    .reverse();

  // Calcular totales para resumen
  const totalCobrosARS = filteredCobros
    .filter((c) => c.moneda === "ARS")
    .reduce((sum, c) => sum + parseFloat(c.monto), 0);

  const totalCobrosUSD = filteredCobros
    .filter((c) => c.moneda === "USD")
    .reduce((sum, c) => sum + parseFloat(c.monto), 0);

  const cobrosEsteMes = filteredCobros.filter(
    (c) => c.fecha.substring(0, 7) === new Date().toISOString().substring(0, 7)
  );

  // ✅ PREPARAR OPCIONES PARA DROPDOWNS
  const trabajadoresOptions = [
    { value: "todos", label: "Todos los trabajadores" },
    ...trabajadores.map((trabajador) => ({
      value: trabajador.id,
      label: `${trabajador.apellido}, ${trabajador.nombre}`,
    })),
  ];

  const conceptosOptions = [
    { value: "todos", label: "Todos los conceptos" },
    ...conceptos.map((concepto) => ({
      value: concepto.id,
      label: concepto.nombre,
    })),
  ];

  const mesesOptions = [
    { value: "todos", label: "Todos los meses" },
    ...mesesUnicos.map((mes) => {
      const [year, month] = mes.split("-");
      return {
        value: mes,
        label: new Date(
          parseInt(year),
          parseInt(month) - 1,
          1
        ).toLocaleDateString("es-ES", { year: "numeric", month: "long" }),
      };
    }),
  ];

  // ✅ STATS DATA PARA RESUMEN COMPACTO
  const statsData = [
    {
      label: "Total Cobros",
      value: filteredCobros.length.toString(),
      icon: Users,
      color: colorVista.solid,
    },
    {
      label: "Este Mes",
      value: cobrosEsteMes.length.toString(),
      icon: Calendar,
      color: "#0ea5e9",
    },
    {
      label: "Total ARS",
      value: `$${totalCobrosARS.toLocaleString()}`,
      icon: DollarSign,
      color: "#10b981",
    },
    {
      label: "Total USD",
      value: `US$${totalCobrosUSD.toLocaleString()}`,
      icon: DollarSign,
      color: "#8b5cf6",
    },
  ];

  // Prevenir scroll shift cuando el modal está abierto
  usePreventScrollShift(showModal);

  return (
    <div
      className="main-container"
      style={{
        padding: isMobile ? "16px 12px 120px 12px" : "20px",
        maxWidth: "100%",
        margin: "0 auto",
        background: "#f8fafc",
        minHeight: "100vh",
        animation: "fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* ✅ HEADER UNIFICADO CON FILTROS */}
      <div
        className="header-section"
        style={{
          marginBottom: "20px",
          animation: "slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "16px",
            marginBottom: "16px",
            minHeight: "56px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: 1,
              minWidth: "200px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                background: colorVista.gradient,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 4px 16px ${colorVista.light}`,
                flexShrink: 0,
              }}
            >
              <TrendingDown size={20} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1
                style={{
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  color: "#0f172a",
                  margin: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Cobros
              </h1>
              <p
                style={{
                  fontSize: "14px",
                  color: "#64748b",
                  margin: 0,
                  fontWeight: "400",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Pagos a trabajadores de {constructora?.nombre}
              </p>
            </div>
          </div>

          {/* Botón desktop unificado - SIEMPRE VISIBLE */}
          {!isMobile && (
            <button
              onClick={() => {
                setEditingCobro(null);
                setShowModal(true);
              }}
              className="button-hover"
              style={{
                background: colorVista.gradient,
                color: "white",
                border: "none",
                padding: "12px 20px",
                borderRadius: "12px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                boxShadow: `0 4px 16px ${colorVista.light}`,
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
                flexShrink: 0,
                minWidth: "max-content",
                whiteSpace: "nowrap",
              }}
            >
              <Plus size={16} />
              Nuevo cobro
            </button>
          )}
        </div>

        {/* ✅ FILTROS UNIFICADOS - BÚSQUEDA Y BOTÓN DE FILTROS */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr auto",
            gap: "16px",
            marginBottom: "20px",
            alignItems: "center",
          }}
        >
          <div style={{ position: "relative" }}>
            <Search
              size={16}
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#64748b",
                zIndex: 1,
              }}
            />
            <input
              type="text"
              placeholder="Buscar por trabajador, concepto o descripción..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus={false}
              autoComplete="off"
              style={{
                width: "100%",
                padding: "14px 16px 14px 44px",
                border: "2px solid #f1f5f9",
                borderRadius: "12px",
                fontSize: "16px",
                WebkitAppearance: "none",
                appearance: "none",
                background: "white",
                boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                outline: "none",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                boxSizing: "border-box",
                touchAction: "manipulation",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#3b82f6";
                e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#f1f5f9";
                e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
              }}
            />
          </div>

          {/* Botón Filtros - Versión compacta para mobile, completa para desktop */}
          <button
            onClick={() => setShowFilterPanel(!showFilterPanel)}
            className="button-hover"
            style={{
              padding: isMobile ? "10px" : (isCliente ? "14px 14px" : "14px 20px"),
              border: isCliente ? "2px solid #f1f5f9" : "2px solid #e2e8f0",
              borderRadius: "12px",
              background: showFilterPanel ? "#f1f5f9" : (hayFiltrosActivos() ? "#fef3c7" : "white"),
              color: showFilterPanel ? "#1e293b" : (hayFiltrosActivos() ? "#92400e" : "#475569"),
              fontSize: isCliente ? "14px" : "13px",
              fontWeight: "600",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: isMobile ? "0px" : (isCliente ? "8px" : "6px"),
              transition: isCliente ? "all 0.2s ease" : "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
              height: isMobile ? "44px" : "auto",
              position: "relative",
              width: isMobile ? "44px" : (isCliente ? "fit-content" : undefined),
            }}
          >
            <Settings size={16} />
            {!isMobile && "Filtros"}
            {hayFiltrosActivos() && (
              <div
                style={{
                  position: "absolute",
                  top: "-4px",
                  right: "-4px",
                  width: "8px",
                  height: "8px",
                  backgroundColor: "#f59e0b",
                  borderRadius: "50%",
                  border: "2px solid white",
                }}
              />
            )}
          </button>
        </div>

        {/* Filtros Adicionales con animación de apertura/cierre - DESKTOP */}
        {!isMobile && (
        <div
          style={{
            maxHeight: showFilterPanel ? "120px" : "0px",
            overflow: showFilterPanel ? "visible" : "hidden",
            transition: "all 0.25s ease-out",
            opacity: showFilterPanel ? 1 : 0,
            marginBottom: showFilterPanel ? "20px" : "0px",
            position: "relative",
            zIndex: 20,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "12px",
              alignItems: "center",
            }}
          >
            <CustomDropdown
              value={filterTrabajador}
              onChange={setFilterTrabajador}
              options={trabajadoresOptions}
              placeholder="Trabajador"
              icon={User}
            />

            <CustomDropdown
              value={filterConcepto}
              onChange={setFilterConcepto}
              options={conceptosOptions}
              placeholder="Concepto"
              icon={FileText}
            />

            <CustomDropdown
              value={filterMonth}
              onChange={setFilterMonth}
              options={mesesOptions}
              placeholder="Mes"
              icon={Calendar}
            />
            
            <CustomDropdown
              value={filterEstado}
              onChange={setFilterEstado}
              options={[
                { value: "activos", label: "Activos" },
                { value: "eliminados", label: "Eliminados" },
              ]}
              placeholder="Estado"
              icon={Filter}
            />
          </div>
        </div>
        )}

        {/* Filtros Adicionales con animación de apertura/cierre - MOBILE */}
        {isMobile && (
        <div
          style={{
            maxHeight: showFilterPanel ? "500px" : "0px",
            overflow: showFilterPanel ? "visible" : "hidden",
            transition: "all 0.25s ease-out",
            opacity: showFilterPanel ? 1 : 0,
            marginBottom: showFilterPanel ? "20px" : "0px",
            position: "relative",
            zIndex: 20,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: "8px",
              alignItems: "center",
              padding: showFilterPanel ? "12px 0" : "0",
            }}
          >
            <CustomDropdown
              value={filterTrabajador}
              onChange={setFilterTrabajador}
              options={trabajadoresOptions}
              placeholder="Trabajador"
              icon={User}
            />

            <CustomDropdown
              value={filterConcepto}
              onChange={setFilterConcepto}
              options={conceptosOptions}
              placeholder="Concepto"
              icon={FileText}
            />

            <CustomDropdown
              value={filterMonth}
              onChange={setFilterMonth}
              options={mesesOptions}
              placeholder="Mes"
              icon={Calendar}
            />
            
            <CustomDropdown
              value={filterEstado}
              onChange={setFilterEstado}
              options={[
                { value: "activos", label: "Activos" },
                { value: "eliminados", label: "Eliminados" },
              ]}
              placeholder="Estado"
              icon={Filter}
            />
          </div>
        </div>
        )}
      </div>

      {/* ✅ STATS COMPACTAS (SOLO DESKTOP) */}
      {!isMobile && (
        <div
          className="stats-section"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "12px",
            marginBottom: "20px",
            animation: "fadeInUp 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {statsData.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div
                key={index}
                className="stat-card"
                style={{
                  background: "white",
                  padding: "12px",
                  borderRadius: "12px",
                  textAlign: "center",
                  border: "1px solid #f1f5f9",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  minHeight: "80px",
                  animation: `slideInRight 0.${
                    8 + index
                  }s cubic-bezier(0.4, 0, 0.2, 1)`,
                }}
              >
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    background: `${stat.color}10`,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 8px",
                  }}
                >
                  <IconComponent size={16} color={stat.color} />
                </div>
                <p
                  style={{
                    fontSize: "10px",
                    color: "#64748b",
                    margin: "0 0 4px 0",
                    fontWeight: "500",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {stat.label}
                </p>
                <p
                  style={{
                    fontSize: "16px",
                    fontWeight: "700",
                    color: "#1e293b",
                    margin: 0,
                  }}
                >
                  {stat.value}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* ✅ CONTENIDO PRINCIPAL */}
      <div
        className="main-content"
        style={{
          animation: "fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {loading ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "80px 20px",
              background: "white",
              borderRadius: "16px",
              border: "1px solid #f1f5f9",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  border: "2px solid #e2e8f0",
                  borderTop: "2px solid #3b82f6",
                  borderRadius: "50%",
                  margin: "0 auto 16px",
                  animation: "spin 1s linear infinite",
                }}
              />
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  color: "#64748b",
                  fontWeight: "500",
                }}
              >
                Cargando...
              </p>
            </div>
          </div>
        ) : filteredCobros.length === 0 ? (
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              padding: isMobile ? "40px 20px" : "60px 40px",
              textAlign: "center",
              boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
              border: "2px dashed #cbd5e1",
            }}
          >
            <TrendingDown
              size={48}
              color="#64748b"
              style={{ marginBottom: "20px" }}
            />
            <h3
              style={{
                fontSize: isMobile ? "18px" : "20px",
                color: "#1e293b",
                marginBottom: "10px",
              }}
            >
              {searchTerm ||
              filterTrabajador !== "todos" ||
              filterConcepto !== "todos" ||
              filterMonth !== "todos"
                ? "No se encontraron cobros"
                : "No hay cobros aún"}
            </h3>
            <p
              style={{
                color: "#64748b",
                fontSize: "15px",
                marginBottom:
                  searchTerm ||
                  filterTrabajador !== "todos" ||
                  filterConcepto !== "todos" ||
                  filterMonth !== "todos"
                    ? "0"
                    : "25px",
              }}
            >
              {searchTerm ||
              filterTrabajador !== "todos" ||
              filterConcepto !== "todos" ||
              filterMonth !== "todos"
                ? "Intenta cambiar los filtros de búsqueda"
                : "Comienza registrando el primer pago a trabajadores"}
            </p>
            {!searchTerm &&
              filterTrabajador === "todos" &&
              filterConcepto === "todos" &&
              filterMonth === "todos" && (
                <button
                  onClick={() => {
                    setEditingCobro(null);
                    setShowModal(true);
                  }}
                  className="button-hover"
                  style={{
                    background: colorVista.gradient,
                    color: "white",
                    border: "none",
                    padding: "14px 28px",
                    borderRadius: "10px",
                    fontSize: "15px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    touchAction: "manipulation",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    margin: "0 auto",
                  }}
                >
                  <Plus size={16} />
                  Registrar Primer Cobro
                </button>
              )}
          </div>
        ) : isMobile ? (
          // ✅ CARDS MÓVILES ESTANDARIZADAS
          <div style={{ padding: "8px" }}>
            {filteredCobros.map((cobro, index) => {
              const trabajador = trabajadores.find(
                (t) => t.id === cobro.trabajador_id
              );
              const concepto = conceptos.find(
                (c) => c.id === cobro.concepto_id
              );

              return (
                <div
                  key={cobro.id}
                  onClick={() => openDetailModal(cobro)}
                  style={{
                    background: "white",
                    border: "1px solid #f1f5f9",
                    borderRadius: "12px",
                    padding: "16px",
                    marginBottom: "8px",
                    cursor: "pointer",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    animation: `slideInRight 0.${
                      5 + (index % 10)
                    }s cubic-bezier(0.4, 0, 0.2, 1)`,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    touchAction: "manipulation",
                  }}
                  onTouchStart={(e) => {
                    e.currentTarget.style.transform = "scale(0.98)";
                    e.currentTarget.style.backgroundColor = "#f8fafc";
                  }}
                  onTouchEnd={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.backgroundColor = "white";
                  }}
                >
                  {/* HEADER UNIFICADO */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "12px",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          color: "#1e293b",
                          marginBottom: "4px",
                        }}
                      >
                        {trabajador?.apellido}, {trabajador?.nombre}
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#64748b",
                          fontWeight: "500",
                        }}
                      >
                        {trabajador?.puesto} ({trabajador?.tipo})
                      </div>
                    </div>
                    <div
                      style={{
                        textAlign: "right",
                        marginLeft: "12px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "800",
                          color: colorVista.solid,
                          marginBottom: "2px",
                        }}
                      >
                        {cobro.moneda} ${Number(cobro.monto).toLocaleString()}
                      </div>
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#64748b",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: "4px",
                        }}
                      >
                        <Calendar size={10} />
                        {formatDateForDisplay(cobro.fecha)}
                      </div>
                    </div>
                  </div>

                  {/* BADGES UNIFICADOS */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      marginBottom: "12px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        background: `${colorVista.solid}10`,
                        color: colorVista.solid,
                        padding: "4px 8px",
                        borderRadius: "6px",
                        fontSize: "11px",
                        fontWeight: "600",
                      }}
                    >
                      {concepto?.nombre || "Sin concepto"}
                    </span>
                    {cobro.generado_desde_entrada && (
                      <span
                        style={{
                          background: "#fef3cd",
                          color: "#92400e",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          fontSize: "9px",
                          fontWeight: "600",
                          display: "flex",
                          alignItems: "center",
                          gap: "2px",
                        }}
                      >
                        <DollarSign size={8} />
                        Auto
                      </span>
                    )}
                    {cobro.metodo_pago && (
                      <span
                        style={{
                          background: "#f0f9ff",
                          color: "#0ea5e9",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          fontSize: "9px",
                          fontWeight: "600",
                        }}
                      >
                        {cobro.metodo_pago}
                      </span>
                    )}
                  </div>

                  {/* DESCRIPCIÓN (si existe) */}
                  {cobro.descripcion && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#64748b",
                        marginBottom: "12px",
                        lineHeight: "1.4",
                      }}
                    >
                      {cobro.descripcion}
                    </div>
                  )}

                  {/* FOOTER UNIFICADO */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      paddingTop: "12px",
                      borderTop: "1px solid #f1f5f9",
                    }}
                  >
                    <div style={{ display: "flex", gap: "4px" }}>
                      {cobro.tiene_comprobante && (
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            openDocumentModal(
                              cobro.comprobante_url,
                              "comprobante",
                              cobro
                            );
                          }}
                          style={{
                            fontSize: "9px",
                            background: "#dcfce7",
                            color: "#166534",
                            padding: "3px 6px",
                            borderRadius: "4px",
                            fontWeight: "500",
                            display: "flex",
                            alignItems: "center",
                            gap: "2px",
                            cursor: "pointer",
                            touchAction: "manipulation",
                          }}
                        >
                          <FileText size={8} />
                          Comp.
                        </span>
                      )}
                      {cobro.tiene_factura && (
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            openDocumentModal(
                              cobro.factura_url,
                              "factura",
                              cobro
                            );
                          }}
                          style={{
                            fontSize: "9px",
                            background: "#fef3cd",
                            color: "#92400e",
                            padding: "3px 6px",
                            borderRadius: "4px",
                            fontWeight: "500",
                            display: "flex",
                            alignItems: "center",
                            gap: "2px",
                            cursor: "pointer",
                            touchAction: "manipulation",
                          }}
                        >
                          <FileText size={8} />
                          Fact.
                        </span>
                      )}
                    </div>

                    <div style={{ display: "flex", gap: "4px" }}>
                      {filterEstado === "activos" && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (cobro.generado_desde_entrada) {
                              showCustomAlert(
                                "info",
                                "No editable",
                                "Este cobro fue generado automáticamente desde una entrada. No se puede editar directamente."
                              );
                              return;
                            }
                            setEditingCobro(cobro);
                            setShowModal(true);
                          }}
                          style={{
                            background: cobro.generado_desde_entrada
                              ? "#f1f5f9"
                              : "#eff6ff",
                            border: "none",
                            borderRadius: "6px",
                            padding: "6px 8px",
                            cursor: cobro.generado_desde_entrada
                              ? "not-allowed"
                              : "pointer",
                            color: cobro.generado_desde_entrada
                              ? "#94a3b8"
                              : "#3b82f6",
                            touchAction: "manipulation",
                            opacity: cobro.generado_desde_entrada ? 0.5 : 1,
                          }}
                        >
                          <Edit size={12} />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (filterEstado === "activos")
                            handleEliminar(
                              cobro.id,
                              !!cobro.entrada_relacionada_id
                            );
                          else
                            handleRestaurar(
                              cobro.id,
                              !!cobro.entrada_relacionada_id
                            );
                        }}
                        style={{
                          background:
                            filterEstado === "activos" ? "#fef2f2" : "#f0fdf4",
                          border: "none",
                          borderRadius: "6px",
                          padding: "6px 8px",
                          cursor: "pointer",
                          color:
                            filterEstado === "activos" ? "#ef4444" : "#22c55e",
                          touchAction: "manipulation",
                        }}
                      >
                        {filterEstado === "activos" ? (
                          <Trash2 size={12} />
                        ) : (
                          <Package size={12} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          // ✅ TABLA DESKTOP UNIFICADA
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
              border: "1px solid #f1f5f9",
            }}
          >
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  minWidth: "1200px",
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: "#f8fafc",
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "#374151",
                        fontSize: "14px",
                      }}
                    >
                      Fecha
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "#374151",
                        fontSize: "14px",
                      }}
                    >
                      Trabajador
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "#374151",
                        fontSize: "14px",
                      }}
                    >
                      Concepto
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "right",
                        fontWeight: "600",
                        color: "#374151",
                        fontSize: "14px",
                      }}
                    >
                      Monto
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "#374151",
                        fontSize: "14px",
                      }}
                    >
                      Descripción
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "#374151",
                        fontSize: "14px",
                      }}
                    >
                      Método
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "left",
                        fontWeight: "600",
                        color: "#374151",
                        fontSize: "14px",
                      }}
                    >
                      Archivos
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        fontWeight: "600",
                        color: "#374151",
                        fontSize: "14px",
                        width: "160px",
                      }}
                    >
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCobros.map((cobro, index) => {
                    const trabajador = trabajadores.find(
                      (t) => t.id === cobro.trabajador_id
                    );
                    const concepto = conceptos.find(
                      (c) => c.id === cobro.concepto_id
                    );

                    return (
                      <tr
                        key={cobro.id}
                        style={{
                          borderBottom:
                            index < filteredCobros.length - 1
                              ? "1px solid #f1f5f9"
                              : "none",
                          transition: "background-color 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.target.parentElement.style.backgroundColor =
                            "#f8fafc";
                        }}
                        onMouseLeave={(e) => {
                          e.target.parentElement.style.backgroundColor =
                            "transparent";
                        }}
                      >
                        <td
                          style={{
                            padding: "16px 20px",
                            color: "#374151",
                            fontSize: "14px",
                            fontWeight: "500",
                          }}
                        >
                          {formatDateForDisplay(cobro.fecha)}
                        </td>
                        <td
                          style={{
                            padding: "16px 20px",
                            color: "#1e293b",
                            fontSize: "14px",
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: "600" }}>
                              {trabajador?.apellido}, {trabajador?.nombre}
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#64748b",
                                fontWeight: "400",
                              }}
                            >
                              {trabajador?.puesto} ({trabajador?.tipo})
                            </div>
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "16px 20px",
                            color: "#1e293b",
                            fontSize: "14px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              flexWrap: "wrap",
                            }}
                          >
                            <span
                              style={{
                                background: `${colorVista.solid}20`,
                                color: colorVista.solid,
                                padding: "4px 8px",
                                borderRadius: "12px",
                                fontSize: "11px",
                                fontWeight: "500",
                              }}
                            >
                              {concepto?.nombre || "Sin concepto"}
                            </span>
                            {cobro.generado_desde_entrada && (
                              <span
                                style={{
                                  background: "#fef3cd",
                                  color: "#92400e",
                                  padding: "2px 6px",
                                  borderRadius: "8px",
                                  fontSize: "9px",
                                  fontWeight: "500",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "2px",
                                }}
                                title={`Generado desde entrada del proyecto ${
                                  cobro.entradas?.proyectos?.codigo || "N/A"
                                }`}
                              >
                                <DollarSign size={8} />
                                Auto
                              </span>
                            )}
                            {cobro.comisiones && (
                              <span
                                style={{
                                  background: "#e0f2fe",
                                  color: "#0369a1",
                                  padding: "2px 6px",
                                  borderRadius: "8px",
                                  fontSize: "9px",
                                  fontWeight: "500",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "2px",
                                }}
                                title={`${cobro.comisiones.salidas?.proveedores_servicios?.nombre} • ${cobro.comisiones.salidas?.proyectos?.codigo}`}
                              >
                                <DollarSign size={8} />
                                {`${
                                  cobro.comisiones.salidas
                                    ?.proveedores_servicios?.nombre ||
                                  "Proveedor"
                                } • ${
                                  cobro.comisiones.salidas?.proyectos?.codigo ||
                                  "Proyecto"
                                }`}
                              </span>
                            )}
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "16px 20px",
                            textAlign: "right",
                            fontSize: "14px",
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontWeight: "700",
                                color: colorVista.solid,
                              }}
                            >
                              {cobro.moneda} $
                              {Number(cobro.monto).toLocaleString()}
                            </div>
                            {cobro.tipo_cambio && cobro.tipo_cambio !== "1" && (
                              <div
                                style={{
                                  fontSize: "9px",
                                  color: "#0ea5e9",
                                  fontWeight: "400",
                                }}
                              >
                                TC: ${cobro.tipo_cambio}
                              </div>
                            )}
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "16px 20px",
                            color: "#64748b",
                            fontSize: "13px",
                            maxWidth: "150px",
                          }}
                        >
                          {cobro.descripcion ? (
                            <div
                              style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              title={cobro.descripcion}
                            >
                              {cobro.descripcion}
                            </div>
                          ) : (
                            <span
                              style={{ color: "#94a3b8", fontStyle: "italic" }}
                            >
                              Sin descripción
                            </span>
                          )}
                          {cobro.generado_desde_entrada &&
                            cobro.entradas?.proyectos && (
                              <div
                                style={{
                                  fontSize: "11px",
                                  color: "#f59e0b",
                                  fontWeight: "500",
                                  marginTop: "2px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <Building2 size={10} />
                                {cobro.entradas.proyectos.codigo}
                              </div>
                            )}
                        </td>
                        <td
                          style={{
                            padding: "16px 20px",
                            color: "#64748b",
                            fontSize: "13px",
                          }}
                        >
                          {cobro.metodo_pago || "No especificado"}
                        </td>
                        <td
                          style={{
                            padding: "16px 20px",
                            color: "#64748b",
                            fontSize: "13px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "4px",
                              flexWrap: "wrap",
                            }}
                          >
                            {cobro.tiene_comprobante && (
                              <span
                                onClick={() =>
                                  openDocumentModal(
                                    cobro.comprobante_url,
                                    "comprobante",
                                    cobro
                                  )
                                }
                                className="action-button"
                                style={{
                                  background: "#dcfce7",
                                  color: "#166534",
                                  padding: "2px 6px",
                                  borderRadius: "8px",
                                  fontSize: "9px",
                                  fontWeight: "500",
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                  border: "1px solid transparent",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "2px",
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = "#bbf7d0";
                                  e.target.style.border = "1px solid #166534";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = "#dcfce7";
                                  e.target.style.border =
                                    "1px solid transparent";
                                }}
                              >
                                <FileText size={8} />
                                Comp.
                              </span>
                            )}
                            {cobro.tiene_factura && (
                              <span
                                onClick={() =>
                                  openDocumentModal(
                                    cobro.factura_url,
                                    "factura",
                                    cobro
                                  )
                                }
                                className="action-button"
                                style={{
                                  background: "#fef3cd",
                                  color: "#92400e",
                                  padding: "2px 6px",
                                  borderRadius: "8px",
                                  fontSize: "9px",
                                  fontWeight: "500",
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                  border: "1px solid transparent",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "2px",
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = "#fed7aa";
                                  e.target.style.border = "1px solid #92400e";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = "#fef3cd";
                                  e.target.style.border =
                                    "1px solid transparent";
                                }}
                              >
                                <FileText size={8} />
                                {cobro.tipo_factura
                                  ? `Fact. ${cobro.tipo_factura}`
                                  : "Fact."}
                              </span>
                            )}
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "16px 20px",
                            textAlign: "center",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "6px",
                              justifyContent: "center",
                            }}
                          >
                            {/* Botón para ver detalles completos */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openDetailModal(cobro);
                              }}
                              className="action-button"
                              style={{
                                background: "#eff6ff",
                                border: "none",
                                borderRadius: "6px",
                                padding: "6px 8px",
                                cursor: "pointer",
                                color: "#1d4ed8",
                                fontSize: "12px",
                                transition: "all 0.2s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = "#dbeafe";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = "#eff6ff";
                              }}
                            >
                              <Eye size={12} />
                            </button>

                            {/* Botón de edición con validación */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (cobro.generado_desde_entrada) {
                                  showCustomAlert(
                                    "info",
                                    "No editable",
                                    "Este cobro fue generado automáticamente desde una entrada. No se puede editar directamente."
                                  );
                                  return;
                                }
                                setEditingCobro(cobro);
                                setShowModal(true);
                              }}
                              className="action-button"
                              style={{
                                background: cobro.generado_desde_entrada
                                  ? "#f1f5f9"
                                  : "#f1f5f9",
                                border: "none",
                                borderRadius: "6px",
                                padding: "6px 8px",
                                cursor: cobro.generado_desde_entrada
                                  ? "not-allowed"
                                  : "pointer",
                                color: cobro.generado_desde_entrada
                                  ? "#94a3b8"
                                  : "#475569",
                                fontSize: "12px",
                                transition: "all 0.2s ease",
                                opacity: cobro.generado_desde_entrada ? 0.5 : 1,
                              }}
                              onMouseEnter={(e) => {
                                if (!cobro.generado_desde_entrada) {
                                  e.target.style.background = "#e2e8f0";
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = "#f1f5f9";
                              }}
                              title={
                                cobro.generado_desde_entrada
                                  ? "No se puede editar - Generado automáticamente"
                                  : "Editar cobro"
                              }
                            >
                              <Edit size={12} />
                            </button>
                            {filterEstado === "activos" ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEliminar(
                                    cobro.id,
                                    !!cobro.entrada_relacionada_id
                                  );
                                }}
                                className="action-button"
                                style={{
                                  background: "#fef2f2",
                                  border: "none",
                                  borderRadius: "6px",
                                  padding: "6px 8px",
                                  cursor: "pointer",
                                  color: "#dc2626",
                                  fontSize: "12px",
                                  transition: "all 0.2s ease",
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = "#fee2e2";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = "#fef2f2";
                                }}
                              >
                                <Trash2 size={12} />
                              </button>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRestaurar(
                                    cobro.id,
                                    !!cobro.entrada_relacionada_id
                                  );
                                }}
                                className="action-button"
                                style={{
                                  background: "#f0fdf4",
                                  border: "none",
                                  borderRadius: "6px",
                                  padding: "6px 8px",
                                  cursor: "pointer",
                                  color: "#16a34a",
                                  fontSize: "12px",
                                  transition: "all 0.2s ease",
                                }}
                              >
                                <Package size={12} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ✅ BOTÓN FLOTANTE MÓVIL UNIFICADO */}
      {isMobile && (
        <div
          style={{
            position: "fixed",
            bottom: "100px",
            right: "20px",
            zIndex: 1000,
          }}
        >
          <button
            onClick={() => {
              setEditingCobro(null);
              setShowModal(true);
            }}
            className="button-hover"
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "28px",
              background: colorVista.gradient,
              color: "white",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              boxShadow: `0 8px 32px ${colorVista.light}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
            }}
            onTouchStart={(e) => {
              e.currentTarget.style.transform = "scale(0.95)";
            }}
            onTouchEnd={(e) => {
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            <Plus size={24} />
          </button>
        </div>
      )}

      {/* Modal para crear/editar cobros */}
      {showModal && (
        <CobroModal
          constructora={constructora}
          usuario={usuario}
          cobro={editingCobro}
          trabajadores={trabajadores}
          conceptos={conceptos}
          cajas={cajas}
          onClose={() => {
            setShowModal(false);
            setEditingCobro(null);
          }}
          onSave={() => {
            setShowModal(false);
            setEditingCobro(null);
            cargarDatos();
            showCustomAlert(
              "success",
              "Éxito",
              editingCobro
                ? "Cobro actualizado correctamente"
                : "Cobro registrado correctamente"
            );
          }}
          isMobile={isMobile}
          showCustomAlert={showCustomAlert}
        />
      )}

      {/* Modal de Vista Detallada */}
      {showDetailModal && selectedCobroDetail && (
        <CobroDetailModal
          cobro={selectedCobroDetail}
          trabajadores={trabajadores}
          conceptos={conceptos}
          cajas={cajas}
          constructora={constructora}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedCobroDetail(null);
          }}
          isMobile={isMobile}
        />
      )}

      {/* Modal de Documento Individual */}
      {showDocumentModal && selectedDocument && (
        <DocumentViewModal
          url={selectedDocument}
          tipo={selectedDocumentType}
          onClose={() => {
            setShowDocumentModal(false);
            setSelectedDocument(null);
            setSelectedDocumentType(null);
          }}
          isMobile={isMobile}
        />
      )}

      {/* ✅ MODAL PERSONALIZADO PARA ALERTAS CON ESC HANDLER */}
      <CustomModal
        isOpen={customModal.isOpen}
        onClose={() => setCustomModal((prev) => ({ ...prev, isOpen: false }))}
        type={customModal.type}
        title={customModal.title}
        message={customModal.message}
        onConfirm={customModal.onConfirm}
        isMobile={isMobile}
      />

      {/* ✅ CSS OBLIGATORIO - ANIMACIONES UNIFICADAS */}
      <style jsx>{`
        /* Configuración anti-zoom */
        input[type="text"],
        input[type="number"],
        input[type="date"],
        input[type="email"],
        input[type="tel"],
        input[type="search"],
        textarea,
        select {
          font-size: 16px !important;
          touch-action: manipulation;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        input:focus,
        textarea:focus,
        select:focus {
          font-size: 16px !important;
          outline: none;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        @media (max-width: 768px) {
          input,
          textarea,
          select,
          button {
            font-size: 16px !important;
            -webkit-appearance: none !important;
            appearance: none !important;
          }
        }

        /* Animaciones estándar */
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .loading-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        /* Hover effects */
        .button-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }

        .action-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        /* Responsive desktop crítico */
        @media (min-width: 769px) {
          .header-section > div:first-child {
            flex-wrap: nowrap !important;
            min-height: 56px;
          }

          .button-hover {
            flex-shrink: 0 !important;
            white-space: nowrap !important;
            min-width: max-content !important;
          }

          .main-container {
            min-width: 1024px;
          }
        }
      `}</style>
    </div>
  );
}

// ✅ MODAL PARA CREAR/EDITAR COBROS CON TODOS LOS LINEAMIENTOS
function CobroModal({
  constructora,
  usuario,
  cobro,
  trabajadores,
  conceptos,
  cajas,
  onClose,
  onSave,
  isMobile,
  showCustomAlert,
}) {
  const [formData, setFormData] = useState({
    trabajador_id: "",
    concepto_id: "",
    caja_origen_id: "",
    monto: "",
    moneda: "ARS",
    metodo_pago: "",
    fecha: new Date().toISOString().split("T")[0],
    descripcion: "",
    comprobante_url: "",
    factura_url: "",
    tiene_comprobante: false,
    tiene_factura: false,
    facturador: "",
    factura_a_nombre_de: "",
    tipo_factura: "C",
    tipo_cambio: "1",
    comision_relacionada_id: "",
  });

  const [guardando, setGuardando] = useState(false);
  const [subiendoArchivos, setSubiendoArchivos] = useState(false);
  const [saldoInsuficiente, setSaldoInsuficiente] = useState(false);
  const [dolarOficial, setDolarOficial] = useState(null);
  const [comisiones, setComisiones] = useState([]);

  const colorVista = getVistaColor();
  const cajasDisponibles = cajas.filter(
    (caja) => caja.moneda === formData.moneda
  );

  // Ancho uniforme SOLO para mobile, mantiene 100% en desktop
  const fieldWidth = isMobile ? "calc(100% - 70px)" : "100%";

  // Función para crear nuevo concepto de cobro
  const crearNuevoConcepto = async (nombre) => {
    try {
      const { data, error } = await supabase
        .from("conceptos_cobro")
        .insert([
          {
            constructora_id: constructora.id,
            nombre: nombre.trim(),
            activo: true,
          },
        ])
        .select();

      if (error) {
        console.error("Error creando concepto:", error);
        showCustomAlert(
          "error",
          "Error",
          "Error al crear concepto: " + error.message
        );
      } else {
        const nuevoConcepto = data[0];
        // Recargar conceptos desde el componente padre
        window.location.reload(); // Temporal - idealmente debería usar un callback
        setFormData((prev) => ({
          ...prev,
          concepto_id: nuevoConcepto.id,
        }));
        showCustomAlert("success", "Éxito", "Concepto creado correctamente");
      }
    } catch (err) {
      console.error("Error inesperado:", err);
      showCustomAlert("error", "Error", "Error inesperado al crear concepto");
    }
  };

  // Helpers de monto con formato AR
  const parseMontoToNumber = (valor) => {
    if (valor === null || valor === undefined) return 0;
    const limpio = String(valor)
      .replace(/\./g, "") // miles
      .replace(/,/g, ".") // decimales
      .replace(/[^0-9.-]/g, "");
    const num = parseFloat(limpio);
    return isNaN(num) ? 0 : num;
  };

  const formatMontoDisplay = (valor) => {
    if (valor === "" || valor === null || valor === undefined) return "";
    const num = typeof valor === "number" ? valor : parseMontoToNumber(valor);
    return num.toLocaleString("es-AR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Formateo en vivo (solo miles, sin decimales mientras escribe)
  const formatMontoLive = (raw) => {
    if (!raw) return "";
    const digits = String(raw).replace(/\D/g, "");
    if (digits.length === 0) return "";
    const intVal = parseInt(digits, 10);
    if (isNaN(intVal)) return "";
    return intVal.toLocaleString("es-AR");
  };

  // ✅ FUNCIÓN PARA MANEJAR INPUT DE CUIT CON FORMATEO AUTOMÁTICO
  const handleCUITChange = (value, field) => {
    const cuitFormateado = formatearCUIT(value);
    setFormData((prev) => ({
      ...prev,
      [field]: cuitFormateado,
    }));
  };

  useEffect(() => {
    if (cobro) {
      setFormData({
        trabajador_id: cobro.trabajador_id || "",
        concepto_id: cobro.concepto_id || "",
        caja_origen_id: cobro.caja_origen_id || "",
        monto: cobro.monto || "",
        moneda: cobro.moneda || "ARS",
        metodo_pago: cobro.metodo_pago || "",
        fecha: formatDateForInput(cobro.fecha),
        descripcion: cobro.descripcion || "",
        comprobante_url: cobro.comprobante_url || "",
        factura_url: cobro.factura_url || "",
        tiene_comprobante: cobro.tiene_comprobante || false,
        tiene_factura: cobro.tiene_factura || false,
        facturador: cobro.facturador || "",
        factura_a_nombre_de: cobro.factura_a_nombre_de || "",
        tipo_factura: cobro.tipo_factura || "A",
        tipo_cambio: cobro.tipo_cambio ? cobro.tipo_cambio.toString() : "1",
        comision_relacionada_id: cobro.comision_relacionada_id || "",
      });
    }
  }, [cobro]);

  useEffect(() => {
    cargarDolarOficial();
    cargarComisiones();
  }, []);

  useEffect(() => {
    if (dolarOficial && !cobro) {
      setFormData((prev) => ({
        ...prev,
        tipo_cambio: dolarOficial.venta.toString(),
      }));
    }
  }, [dolarOficial, cobro]);

  const cargarDolarOficial = async () => {
    try {
      const response = await fetch("https://dolarapi.com/v1/dolares/oficial");
      const data = await response.json();
      setDolarOficial(data);
      console.log("Dólar oficial cargado en modal:", data.venta);
    } catch (error) {
      console.error("Error cargando dólar oficial:", error);
    }
  };

  const cargarComisiones = async () => {
    try {
      // 1) Traer comisiones con entrada creada, de la constructora y (si hay) misma moneda del cobro
      const { data: comisionesData, error: errCom } = await supabase
        .from("comisiones")
        .select(
          `
          id,
          monto_comision,
          moneda,
          entrada_relacionada_id,
          salidas!inner (
            id,
            proveedores_servicios!inner (nombre, cuit),
            proyectos!inner (codigo, apellido_cliente)
          )
        `
        )
        .eq("constructora_id", constructora.id)
        .eq("activo", true)
        .is("deleted_at", null)
        .not("entrada_relacionada_id", "is", null)
        .order("created_at", { ascending: false });

      if (errCom) {
        console.error("Error cargando comisiones:", errCom);
        return;
      }

      // Filtrar por moneda y asegurar que la ENTRADA relacionada esté ACTIVA y no eliminada
      const comisionesFiltradasMoneda = (comisionesData || []).filter(
        (c) => !formData.moneda || c.moneda === formData.moneda
      );

      // Validar entradas relacionadas
      const entradaIds = comisionesFiltradasMoneda
        .map((c) => c.entrada_relacionada_id)
        .filter(Boolean);
      let entradasValidasSet = new Set();
      if (entradaIds.length > 0) {
        const { data: entradasVinculadas, error: errEntr } = await supabase
          .from("entradas")
          .select("id, activo, deleted_at")
          .in("id", entradaIds);
        if (!errEntr && entradasVinculadas) {
          entradasVinculadas.forEach((e) => {
            if (e && e.activo === true && e.deleted_at == null) {
              entradasValidasSet.add(e.id);
            }
          });
        }
      }

      const comisionesValidas = comisionesFiltradasMoneda.filter((c) =>
        entradasValidasSet.has(c.entrada_relacionada_id)
      );
      const comisionIds = comisionesValidas.map((c) => c.id);

      // 2) Sumar cobros ya hechos por comisión
      let sumasPorComision = {};
      if (comisionIds.length > 0) {
        const { data: cobrosData, error: errCobros } = await supabase
          .from("cobros_trabajadores")
          .select("comision_relacionada_id, monto")
          .eq("constructora_id", constructora.id)
          .in("comision_relacionada_id", comisionIds);
        if (!errCobros && cobrosData) {
          cobrosData.forEach((r) => {
            if (!r.comision_relacionada_id) return;
            sumasPorComision[r.comision_relacionada_id] =
              (sumasPorComision[r.comision_relacionada_id] || 0) +
              Number(r.monto || 0);
          });
        }
      }

      // 3) Contar socios activos
      let cantidadSocios = 0;
      {
        const { data: sociosData, error: errSoc } = await supabase
          .from("trabajadores")
          .select("id")
          .eq("constructora_id", constructora.id)
          .eq("tipo", "socio")
          .eq("activo", true)
          .is("deleted_at", null);
        if (!errSoc && sociosData) cantidadSocios = sociosData.length;
      }

      // 4) Calcular restante y armar opciones (solo restante > 0)
      const opciones = comisionesValidas
        .map((c) => {
          const total = Number(c.monto_comision || 0);
          const cobrado = Number(sumasPorComision[c.id] || 0);
          const restante = Math.max(total - cobrado, 0);
          return {
            value: c.id,
            label: `${c.salidas.proveedores_servicios.nombre} • ${
              c.salidas.proyectos.codigo
            } (${
              c.salidas.proyectos.apellido_cliente
            }) — Total: ${total.toLocaleString("es-AR", {
              style: "currency",
              currency: c.moneda,
            })} — Restante: ${restante.toLocaleString("es-AR", {
              style: "currency",
              currency: c.moneda,
            })}`,
            comision: c,
            meta: { total, restante, cantidadSocios },
          };
        })
        .filter((opt) => opt.meta.restante > 0);

      setComisiones(opciones);
    } catch (error) {
      console.error("Error cargando comisiones:", error);
    }
  };

  useEffect(() => {
    if (formData.caja_origen_id && formData.monto) {
      verificarSaldoSuficiente();
    } else {
      setSaldoInsuficiente(false);
    }
  }, [formData.caja_origen_id, formData.monto, cajas]);

  // Efecto para manejar selección de comisión
  useEffect(() => {
    if (formData.comision_relacionada_id) {
      const comisionSeleccionada = comisiones.find(
        (c) => c.value === formData.comision_relacionada_id
      );
      if (comisionSeleccionada) {
        const comision = comisionSeleccionada.comision;
        const { total, restante, cantidadSocios } =
          comisionSeleccionada.meta || {};

        // Validar que la moneda coincida
        if (formData.moneda !== comision.moneda) {
          showCustomAlert(
            "error",
            "Error de moneda",
            `La comisión seleccionada es en ${comision.moneda} pero el cobro está en ${formData.moneda}. Deben coincidir.`
          );
          setFormData((prev) => ({ ...prev, comision_relacionada_id: "" }));
          return;
        }

        // Advertencia informativa y sugerencia por socio
        if (typeof total === "number" && typeof restante === "number") {
          const cuotaPorSocioTotal =
            cantidadSocios > 0 ? total / cantidadSocios : total;
          const sugerido = Math.min(cuotaPorSocioTotal, restante);
          showCustomAlert(
            "info",
            "Comisión seleccionada",
            `Monto total: ${total.toLocaleString("es-AR", {
              style: "currency",
              currency: comision.moneda,
            })}\nRestante para socios: ${restante.toLocaleString("es-AR", {
              style: "currency",
              currency: comision.moneda,
            })}${
              cantidadSocios > 0
                ? `\nSugerido por socio (${cantidadSocios}, basado en total): ${sugerido.toLocaleString(
                    "es-AR",
                    { style: "currency", currency: comision.moneda }
                  )}`
                : ""
            }`
          );
        }

        // Autocompletar monto con sugerencia (restante por socio si hay socios, si no, restante)
        setFormData((prev) => ({
          ...prev,
          monto: (() => {
            if (typeof total === "number" && typeof restante === "number") {
              const cuotaPorSocioTotal =
                cantidadSocios > 0 ? total / cantidadSocios : total;
              const sugerido = Math.min(cuotaPorSocioTotal, restante);
              return formatMontoDisplay(sugerido);
            }
            const base = Number(comision.monto_comision || 0);
            return formatMontoDisplay(base);
          })(),
        }));
      }
    }
  }, [formData.comision_relacionada_id, formData.moneda, comisiones]);

  useEffect(() => {
    if (formData.moneda && cajasDisponibles.length > 0) {
      if (cobro) {
        console.log(
          "📝 MODO EDICIÓN - Manteniendo caja original:",
          formData.caja_origen_id
        );
        return;
      }

      if (!formData.caja_origen_id) {
        const cajaEmpresa = cajasDisponibles.find(
          (caja) =>
            caja.tipo_caja === "empresa" && caja.moneda === formData.moneda
        );

        if (cajaEmpresa) {
          console.log(
            "🎯 AUTOSELECCIONANDO para nuevo cobro:",
            cajaEmpresa.nombre
          );
          setFormData((prev) => ({
            ...prev,
            caja_origen_id: cajaEmpresa.id,
          }));
        }
      }
    }
  }, [formData.moneda, cajasDisponibles.length, cobro]);

  // ✅ AUTOCOMPLETAR CUIT DEL TRABAJADOR CUANDO SELECCIONA
  useEffect(() => {
    if (formData.trabajador_id && formData.tiene_factura) {
      const trabajadorSeleccionado = trabajadores.find(
        (t) => t.id === formData.trabajador_id
      );
      if (trabajadorSeleccionado?.cuit) {
        setFormData((prev) => ({
          ...prev,
          facturador: formatearCUIT(trabajadorSeleccionado.cuit),
        }));
      }
    }
  }, [formData.trabajador_id, formData.tiene_factura, trabajadores]);

  // ✅ AUTOCOMPLETAR CUIT DEL PROVEEDOR EN "CUIT a nombre de" SI ES COMISIÓN RELACIONADA Y SE ACTIVA FACTURA
  useEffect(() => {
    const conceptoSel = conceptos.find((c) => c.id === formData.concepto_id);
    const esComisiones =
      conceptoSel?.nombre?.trim()?.toLowerCase() === "comisiones";
    if (
      formData.tiene_factura &&
      esComisiones &&
      formData.comision_relacionada_id
    ) {
      const sel = comisiones.find(
        (c) => c.value === formData.comision_relacionada_id
      );
      const cuitProv = sel?.comision?.salidas?.proveedores_servicios?.cuit;
      if (cuitProv) {
        setFormData((prev) => ({
          ...prev,
          // Facturador: CUIT del trabajador (se maneja en otro efecto)
          // A nombre de: CUIT del proveedor
          factura_a_nombre_de: formatearCUIT(cuitProv),
        }));
        return;
      }
    }
    // Si no aplica, no tocar
  }, [
    formData.tiene_factura,
    formData.comision_relacionada_id,
    formData.concepto_id,
    comisiones,
    conceptos,
  ]);

  const verificarSaldoSuficiente = () => {
    const cajaOrigen = cajas.find((c) => c.id === formData.caja_origen_id);
    const montoOperacion = parseMontoToNumber(formData.monto);

    if (cajaOrigen && montoOperacion) {
      const saldoActual = parseFloat(cajaOrigen.balance_actual) || 0;
      setSaldoInsuficiente(montoOperacion > saldoActual);
    }
  };

  const handleFileUpload = async (file, tipo) => {
    if (!file) return;

    if (!file.type.startsWith("image/") && !file.type.includes("pdf")) {
      showCustomAlert(
        "error",
        "Error",
        "Por favor selecciona un archivo de imagen o PDF"
      );
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      showCustomAlert(
        "error",
        "Error",
        "El archivo es muy grande. Máximo 10MB"
      );
      return;
    }

    setSubiendoArchivos(true);

    try {
      const fileExt = file.name.split(".").pop().toLowerCase();
      const timestamp = Date.now();
      const bucketName = tipo === "comprobante" ? "comprobantes" : "facturas";
      const fileName = `cobro-${constructora.id}-${timestamp}.${fileExt}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from(bucketName)
        .upload(fileName, file, {
          upsert: true,
          contentType: file.type,
          cacheControl: "3600",
        });

      if (uploadError) {
        console.error(`Error en upload ${tipo}:`, uploadError);
        throw uploadError;
      }

      const { data: urlData } = supabase.storage
        .from(bucketName)
        .getPublicUrl(fileName);

      const finalUrl = urlData.publicUrl;

      setFormData((prev) => ({
        ...prev,
        [`${tipo}_url`]: finalUrl,
        [`tiene_${tipo}`]: true,
      }));
    } catch (err) {
      console.error(`Error subiendo ${tipo}:`, err);
      showCustomAlert(
        "error",
        "Error",
        `Error al subir ${tipo}: ` + err.message
      );
    }

    setSubiendoArchivos(false);
  };


  const handleSave = async () => {
    if (
      !formData.trabajador_id ||
      !formData.concepto_id ||
      !formData.caja_origen_id ||
      !formData.monto ||
      !formData.fecha
    ) {
      showCustomAlert(
        "error",
        "Error",
        "Por favor completa todos los campos obligatorios"
      );
      return;
    }

    if (parseFloat(formData.monto) <= 0) {
      showCustomAlert("error", "Error", "El monto debe ser mayor a 0");
      return;
    }

    if (!formData.tipo_cambio || parseFloat(formData.tipo_cambio) <= 0) {
      showCustomAlert("error", "Error", "El tipo de cambio debe ser mayor a 0");
      return;
    }

    // Confirmar si saldo insuficiente
    if (saldoInsuficiente) {
      showCustomAlert(
        "confirm",
        "Saldo insuficiente",
        "El monto supera el saldo disponible. Se puede continuar pero la caja quedará en negativo. ¿Deseas continuar?",
        () => proceedWithSave()
      );
      return;
    }

    await proceedWithSave();
  };

  const proceedWithSave = async () => {
    setGuardando(true);

    try {
      const dataToSave = {
        constructora_id: constructora.id,
        trabajador_id: formData.trabajador_id,
        concepto_id: formData.concepto_id,
        caja_origen_id: formData.caja_origen_id,
        monto: parseMontoToNumber(formData.monto),
        moneda: formData.moneda,
        metodo_pago: formData.metodo_pago.trim() || null,
        fecha: formData.fecha,
        descripcion: formData.descripcion.trim() || null,
        comprobante_url: formData.comprobante_url || null,
        factura_url: formData.factura_url || null,
        tiene_comprobante: formData.tiene_comprobante,
        tiene_factura: formData.tiene_factura,
        facturador: formData.facturador.trim() || null,
        factura_a_nombre_de: formData.factura_a_nombre_de.trim() || null,
        tipo_factura: formData.tiene_factura
          ? formData.tipo_factura || "A"
          : null,
        tipo_cambio: parseFloat(formData.tipo_cambio),
        comision_relacionada_id: formData.comision_relacionada_id || null,
      };

      // Agregar campos de auditoría
      if (!cobro) {
        // Creación: setear created_by y updated_by
        dataToSave.created_by = usuario.id;
        dataToSave.updated_by = usuario.id;
        console.log(
          "🆕 Creando cobro - created_by:",
          usuario.id,
          "updated_by:",
          usuario.id
        );
      } else {
        // Actualización: solo setear updated_by
        dataToSave.updated_by = usuario.id;
        console.log("✏️ Actualizando cobro - updated_by:", usuario.id);
      }

      let result;
      if (cobro) {
        result = await supabase
          .from("cobros_trabajadores")
          .update(dataToSave)
          .eq("id", cobro.id)
          .select();
      } else {
        result = await supabase
          .from("cobros_trabajadores")
          .insert([dataToSave])
          .select();
      }

      const { data, error } = result;

      if (error) {
        console.error("Error guardando cobro:", error);
        showCustomAlert("error", "Error", "Error al guardar: " + error.message);
      } else {
        await actualizarBalanceManual();
        onSave();
      }
    } catch (err) {
      console.error("Error:", err);
      showCustomAlert("error", "Error", "Error: " + err.message);
    }

    setGuardando(false);
  };

  const actualizarBalanceManual = async () => {
    try {
      const monto = parseFloat(formData.monto);

      if (cobro) {
        const montoAnterior = parseFloat(cobro.monto);
        const cajaAnterior = cobro.caja_origen_id;

        if (
          montoAnterior === monto &&
          cajaAnterior === formData.caja_origen_id
        ) {
          console.log("✅ No cambió monto ni caja - NO actualizar balances");
          return;
        }

        console.log("⚠️ Detectados cambios - Actualizando balances...");

        if (cajaAnterior) {
          const { data: cajaOrigen } = await supabase
            .from("cajas")
            .select("balance_actual")
            .eq("id", cajaAnterior)
            .single();

          if (cajaOrigen) {
            const nuevoBalanceOrigen =
              parseFloat(cajaOrigen.balance_actual) + montoAnterior;

            await supabase
              .from("cajas")
              .update({ balance_actual: nuevoBalanceOrigen })
              .eq("id", cajaAnterior);
          }
        }
      }

      if (formData.caja_origen_id) {
        const { data: cajaDestino } = await supabase
          .from("cajas")
          .select("balance_actual")
          .eq("id", formData.caja_origen_id)
          .single();

        if (cajaDestino) {
          const nuevoBalanceDestino =
            parseFloat(cajaDestino.balance_actual) - monto;

          await supabase
            .from("cajas")
            .update({ balance_actual: nuevoBalanceDestino })
            .eq("id", formData.caja_origen_id);
        }
      }
    } catch (err) {
      console.error("❌ Error en actualización de balances:", err);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: isMobile ? "10px 10px 80px 10px" : "40px",
        animation: "fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          width: "100%",
          maxWidth: "800px",
          maxHeight: isMobile ? "calc(100vh - 160px)" : "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* ✅ HEADER UNIFICADO */}
        <div
          style={{
            background: colorVista.gradient,
            color: "white",
            padding: isMobile ? "20px" : "25px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: 1,
              minWidth: 0,
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                background: "rgba(255,255,255,0.2)",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <TrendingDown size={20} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2
                style={{
                  margin: "0 0 5px 0",
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {cobro ? "Editar cobro" : "Nuevo cobro"}
              </h2>
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  opacity: 0.9,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {cobro
                  ? "Modificar pago a trabajador"
                  : "Registrar un nuevo pago a trabajador"}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="button-hover"
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "10px",
              padding: "8px 12px",
              color: "white",
              fontSize: "16px",
              cursor: "pointer",
              fontWeight: "600",
              flexShrink: 0,
              minWidth: "max-content",
              whiteSpace: "nowrap",
              touchAction: "manipulation",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Contenido */}
        <div
          style={{
            flex: 1,
            padding: isMobile ? "20px" : "25px",
            overflowY: "auto",
            overflowX: "hidden",
            boxSizing: "border-box",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "grid",
              gap: "20px",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            {/* Trabajador */}
            <div style={{ width: fieldWidth, maxWidth: fieldWidth }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Trabajador *
              </label>
              <CustomDropdown
                value={formData.trabajador_id}
                onChange={(value) =>
                  setFormData({ ...formData, trabajador_id: value })
                }
                options={[
                  { value: "", label: "Selecciona un trabajador" },
                  ...trabajadores.map((trabajador) => ({
                    value: trabajador.id,
                    label: `${trabajador.apellido}, ${trabajador.nombre}`,
                  })),
                ]}
                placeholder="Selecciona un trabajador"
                icon={User}
              />
            </div>

            {/* Concepto */}
            <div style={{ width: fieldWidth, maxWidth: fieldWidth }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Concepto *
              </label>

              <CustomDropdown
                value={formData.concepto_id}
                onChange={(value) =>
                  setFormData({ ...formData, concepto_id: value })
                }
                options={[
                  { value: "", label: "Selecciona un concepto" },
                  ...conceptos.map((concepto) => ({
                    value: concepto.id,
                    label: concepto.nombre,
                  })),
                ]}
                placeholder="Selecciona un concepto"
                icon={FileText}
                canCreate={true}
                onCreateNew={crearNuevoConcepto}
                createText="Crear concepto"
              />
            </div>

            {/* Comisión Relacionada (solo si concepto es 'Comisiones') */}
            {(() => {
              const conceptoSel = conceptos.find(
                (c) => c.id === formData.concepto_id
              );
              const esComisiones =
                conceptoSel?.nombre?.trim()?.toLowerCase() === "comisiones";
              if (!esComisiones) return null;
              return (
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Comisión Relacionada
                  </label>
                  <CustomDropdown
                    value={formData.comision_relacionada_id}
                    onChange={(value) =>
                      setFormData({
                        ...formData,
                        comision_relacionada_id: value,
                      })
                    }
                    options={[
                      { value: "", label: "Sin comisión relacionada" },
                      ...comisiones,
                    ]}
                    placeholder="Selecciona una comisión"
                    icon={DollarSign}
                  />
                </div>
              );
            })()}

            {/* Monto y Moneda */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr",
                gap: "12px",
                width: fieldWidth,
                maxWidth: fieldWidth,
                boxSizing: "border-box",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Monto *
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formData.monto}
                  onChange={(e) => {
                    const raw = e.target.value;
                    // Si ya tiene coma (decimales), no re-formatear en vivo
                    if (raw.includes(",")) {
                      setFormData({
                        ...formData,
                        monto: raw.replace(/[^0-9.,]/g, ""),
                      });
                      return;
                    }
                    // Formateo en vivo de miles sobre la parte entera
                    const live = formatMontoLive(raw);
                    setFormData({ ...formData, monto: live });
                  }}
                  onBlur={() =>
                    setFormData((prev) => {
                      // Si ya tiene coma, conservar tal cual
                      if (String(prev.monto).includes(",")) return prev;
                      // Agregar decimales al salir
                      return { ...prev, monto: formatMontoDisplay(prev.monto) };
                    })
                  }
                  placeholder="0"
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "10px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                    outline: "none",
                    WebkitAppearance: "none",
                    appearance: "none",
                    touchAction: "manipulation",
                  }}
                />

                {saldoInsuficiente && (
                  <div
                    style={{
                      background: "#fef2f2",
                      border: "1px solid #fecaca",
                      borderRadius: "6px",
                      padding: "8px",
                      marginTop: "4px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <AlertCircle size={12} color="#dc2626" />
                    <p
                      style={{
                        fontSize: "11px",
                        color: "#dc2626",
                        margin: 0,
                        fontWeight: "500",
                      }}
                    >
                      El monto supera el saldo disponible. Se puede continuar
                      pero la caja quedará en negativo.
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Moneda *
                </label>
                <CustomDropdown
                  value={formData.moneda}
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      moneda: value,
                      caja_origen_id: "",
                    })
                  }
                  options={[
                    { value: "ARS", label: "ARS - Pesos Argentinos" },
                    { value: "USD", label: "USD - Dólares" },
                  ]}
                  placeholder="Selecciona moneda"
                  icon={DollarSign}
                />
              </div>
            </div>

            {/* Tipo de cambio */}
            <div style={{ width: fieldWidth, maxWidth: fieldWidth }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <DollarSign size={16} />
                Tipo de cambio *
                {dolarOficial && (
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#059669",
                      fontWeight: "500",
                      background: "#dcfce7",
                      padding: "2px 6px",
                      borderRadius: "4px",
                    }}
                  >
                    Oficial: ${dolarOficial.venta}
                  </span>
                )}
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.tipo_cambio}
                onChange={(e) =>
                  setFormData({ ...formData, tipo_cambio: e.target.value })
                }
                placeholder="1.00"
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "2px solid #e2e8f0",
                  borderRadius: "10px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  WebkitAppearance: "none",
                  appearance: "none",
                  touchAction: "manipulation",
                }}
              />
            </div>

            {/* Caja de origen */}
            {cajasDisponibles.length > 0 && (
              <div style={{ width: fieldWidth, maxWidth: fieldWidth }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Wallet size={16} />
                  Caja de origen *
                </label>
                <CustomDropdown
                  value={formData.caja_origen_id}
                  onChange={(value) =>
                    setFormData({ ...formData, caja_origen_id: value })
                  }
                  options={[
                    { value: "", label: "Selecciona una caja" },
                    ...cajasDisponibles.map((caja) => ({
                      value: caja.id,
                      label: `${caja.nombre} ${caja.moneda} ($${parseFloat(
                        caja.balance_actual || 0
                      ).toLocaleString()})`,
                    })),
                  ]}
                  placeholder="Selecciona una caja"
                  icon={Wallet}
                />
              </div>
            )}

            {/* Método de pago */}
            <div style={{ width: fieldWidth, maxWidth: fieldWidth }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Método de pago
              </label>
              <CustomDropdown
                value={formData.metodo_pago}
                onChange={(value) =>
                  setFormData({ ...formData, metodo_pago: value })
                }
                options={[
                  { value: "", label: "Selecciona método de pago" },
                  { value: "Efectivo", label: "Efectivo" },
                  { value: "Transferencia", label: "Transferencia" },
                  { value: "Cheque", label: "Cheque" },
                  { value: "Otros", label: "Otros" },
                ]}
                placeholder="Selecciona método de pago"
                icon={DollarSign}
              />
            </div>

            {/* Fecha */}
            <div style={{ width: fieldWidth, maxWidth: fieldWidth }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Calendar size={16} />
                Fecha *
              </label>
              <input
                type="date"
                value={formData.fecha}
                onChange={(e) =>
                  setFormData({ ...formData, fecha: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "2px solid #e2e8f0",
                  borderRadius: "10px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  WebkitAppearance: "none",
                  appearance: "none",
                  touchAction: "manipulation",
                }}
              />
            </div>

            {/* Descripción */}
            <div style={{ width: fieldWidth, maxWidth: fieldWidth }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Descripción
              </label>
              <textarea
                value={formData.descripcion}
                onChange={(e) =>
                  setFormData({ ...formData, descripcion: e.target.value })
                }
                placeholder="Descripción adicional del pago..."
                rows="3"
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "2px solid #e2e8f0",
                  borderRadius: "10px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  resize: "vertical",
                  WebkitAppearance: "none",
                  appearance: "none",
                  touchAction: "manipulation",
                }}
              />
            </div>

            {/* Archivos - Comprobante y Factura */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: "16px",
                width: fieldWidth,
                maxWidth: fieldWidth,
                boxSizing: "border-box",
              }}
            >
              {/* Comprobante */}
              <div
                style={{
                  background: "#f8fafc",
                  padding: "16px",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  width: "100%",
                  boxSizing: "border-box",
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "12px",
                  }}
                >
                  <label
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <FileText size={16} />
                    Comprobante
                  </label>
                  <button
                    type="button"
                    aria-pressed={Boolean(formData.tiene_comprobante)}
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        tiene_comprobante: !prev.tiene_comprobante,
                        comprobante_url: !prev.tiene_comprobante
                          ? prev.comprobante_url
                          : "",
                      }))
                    }
                    style={{
                      width: 44,
                      height: 24,
                      borderRadius: 9999,
                      border:
                        "1px solid " +
                        (formData.tiene_comprobante
                          ? colorVista.solid
                          : "#cbd5e1"),
                      background: formData.tiene_comprobante
                        ? colorVista.solid
                        : "#e2e8f0",
                      position: "relative",
                      cursor: "pointer",
                      transition: "all .2s ease",
                      outline: "none",
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        top: 2,
                        left: formData.tiene_comprobante ? 22 : 2,
                        width: 18,
                        height: 18,
                        borderRadius: "9999px",
                        background: "white",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                        transition: "left .2s ease",
                      }}
                    />
                  </button>
                </div>

                {formData.tiene_comprobante && (
                  <div
                    style={{
                      border: "2px dashed #cbd5e1",
                      borderRadius: "10px",
                      padding: "16px",
                      textAlign: "center",
                      background: "white",
                    }}
                  >
                    {formData.comprobante_url ? (
                      <div>
                        <CheckCircle2
                          size={24}
                          color="#10b981"
                          style={{ marginBottom: "8px" }}
                        />
                        <p
                          style={{
                            fontSize: "12px",
                            color: "#10b981",
                            margin: "0 0 8px 0",
                            fontWeight: "600",
                          }}
                        >
                          Comprobante subido
                        </p>
                        <div
                          style={{
                            display: "flex",
                            gap: "6px",
                            justifyContent: "center",
                          }}
                        >
                          <button
                            onClick={() =>
                              window.open(formData.comprobante_url, "_blank")
                            }
                            className="button-hover"
                            style={{
                              background: "none",
                              border: "1px solid #10b981",
                              borderRadius: "6px",
                              padding: "4px 8px",
                              color: "#10b981",
                              fontSize: "11px",
                              cursor: "pointer",
                              transition:
                                "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                              touchAction: "manipulation",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <Eye size={10} />
                            Ver
                          </button>
                          <button
                            onClick={() =>
                              setFormData({ ...formData, comprobante_url: "" })
                            }
                            className="button-hover"
                            style={{
                              background: "none",
                              border: "1px solid #ef4444",
                              borderRadius: "6px",
                              padding: "4px 8px",
                              color: "#ef4444",
                              fontSize: "11px",
                              cursor: "pointer",
                              transition:
                                "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                              touchAction: "manipulation",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <X size={10} />
                            Quitar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <input
                          type="file"
                          id="comprobante-cobro-upload"
                          accept="image/*,application/pdf"
                          onChange={(e) =>
                            handleFileUpload(e.target.files[0], "comprobante")
                          }
                          style={{ display: "none" }}
                          disabled={subiendoArchivos}
                        />
                        <label
                          htmlFor="comprobante-cobro-upload"
                          className="button-hover"
                          style={{
                            background: subiendoArchivos
                              ? "#94a3b8"
                              : "#10b981",
                            color: "white",
                            padding: "8px 12px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "600",
                            cursor: subiendoArchivos
                              ? "not-allowed"
                              : "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                            touchAction: "manipulation",
                          }}
                        >
                          {subiendoArchivos ? (
                            <Loader2 size={12} className="loading-pulse" />
                          ) : (
                            <Upload size={12} />
                          )}
                          {subiendoArchivos
                            ? "Subiendo..."
                            : "Seleccionar comprobante"}
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Factura */}
              <div
                style={{
                  background: "#f8fafc",
                  padding: "16px",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  width: "100%",
                  boxSizing: "border-box",
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "12px",
                  }}
                >
                  <label
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <FileText size={16} />
                    Factura
                  </label>
                  <button
                    type="button"
                    aria-pressed={Boolean(formData.tiene_factura)}
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        tiene_factura: !prev.tiene_factura,
                        factura_url: !prev.tiene_factura
                          ? prev.factura_url
                          : "",
                      }))
                    }
                    style={{
                      width: 44,
                      height: 24,
                      borderRadius: 9999,
                      border:
                        "1px solid " +
                        (formData.tiene_factura ? colorVista.solid : "#cbd5e1"),
                      background: formData.tiene_factura
                        ? colorVista.solid
                        : "#e2e8f0",
                      position: "relative",
                      cursor: "pointer",
                      transition: "all .2s ease",
                      outline: "none",
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        top: 2,
                        left: formData.tiene_factura ? 22 : 2,
                        width: 18,
                        height: 18,
                        borderRadius: "9999px",
                        background: "white",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                        transition: "left .2s ease",
                      }}
                    />
                  </button>
                </div>

                {formData.tiene_factura && (
                  <div>
                    {/* Upload factura */}
                    <div
                      style={{
                        border: "2px dashed #cbd5e1",
                        borderRadius: "10px",
                        padding: "16px",
                        textAlign: "center",
                        background: "white",
                        marginBottom: "12px",
                      }}
                    >
                      {formData.factura_url ? (
                        <div>
                          <CheckCircle2
                            size={24}
                            color="#10b981"
                            style={{ marginBottom: "8px" }}
                          />
                          <p
                            style={{
                              fontSize: "12px",
                              color: "#10b981",
                              margin: "0 0 8px 0",
                              fontWeight: "600",
                            }}
                          >
                            Factura subida
                          </p>
                          <div
                            style={{
                              display: "flex",
                              gap: "6px",
                              justifyContent: "center",
                            }}
                          >
                            <button
                              onClick={() =>
                                window.open(formData.factura_url, "_blank")
                              }
                              className="button-hover"
                              style={{
                                background: "none",
                                border: "1px solid #10b981",
                                borderRadius: "6px",
                                padding: "4px 8px",
                                color: "#10b981",
                                fontSize: "11px",
                                cursor: "pointer",
                                transition:
                                  "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                touchAction: "manipulation",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <Eye size={10} />
                              Ver
                            </button>
                            <button
                              onClick={() =>
                                setFormData({ ...formData, factura_url: "" })
                              }
                              className="button-hover"
                              style={{
                                background: "none",
                                border: "1px solid #ef4444",
                                borderRadius: "6px",
                                padding: "4px 8px",
                                color: "#ef4444",
                                fontSize: "11px",
                                cursor: "pointer",
                                transition:
                                  "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                touchAction: "manipulation",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <X size={10} />
                              Quitar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <input
                            type="file"
                            id="factura-cobro-upload"
                            accept="image/*,application/pdf"
                            onChange={(e) =>
                              handleFileUpload(e.target.files[0], "factura")
                            }
                            style={{ display: "none" }}
                            disabled={subiendoArchivos}
                          />
                          <label
                            htmlFor="factura-cobro-upload"
                            className="button-hover"
                            style={{
                              background: subiendoArchivos
                                ? "#94a3b8"
                                : "#10b981",
                              color: "white",
                              padding: "8px 12px",
                              borderRadius: "6px",
                              fontSize: "12px",
                              fontWeight: "600",
                              cursor: subiendoArchivos
                                ? "not-allowed"
                                : "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              transition:
                                "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                              touchAction: "manipulation",
                            }}
                          >
                            {subiendoArchivos ? (
                              <Loader2 size={12} className="loading-pulse" />
                            ) : (
                              <Upload size={12} />
                            )}
                            {subiendoArchivos
                              ? "Subiendo..."
                              : "Seleccionar comprobante"}
                          </label>
                        </div>
                      )}
                    </div>

                    {/* ✅ DATOS DE FACTURACIÓN CON FORMATEO AUTOMÁTICO */}
                    <div
                      style={{
                        display: "grid",
                        gap: "8px",
                        width: fieldWidth,
                        maxWidth: fieldWidth,
                      }}
                    >
                      {/* Tipo de factura */}
                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: 4,
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#64748b",
                          }}
                        >
                          Tipo de factura
                        </label>
                        <CustomDropdown
                          value={formData.tipo_factura}
                          onChange={(value) =>
                            setFormData({ ...formData, tipo_factura: value })
                          }
                          options={[
                            { value: "A", label: "Factura A" },
                            { value: "B", label: "Factura B" },
                            { value: "C", label: "Factura C" },
                          ]}
                          placeholder="Selecciona tipo de factura"
                          icon={FileText}
                        />
                      </div>
                      <input
                        type="text"
                        placeholder="CUIT Facturador (XX-XXXXXXXX-X)"
                        value={formData.facturador}
                        onChange={(e) =>
                          handleCUITChange(e.target.value, "facturador")
                        }
                        maxLength="13"
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          fontSize: "12px",
                          boxSizing: "border-box",
                          WebkitAppearance: "none",
                          appearance: "none",
                          touchAction: "manipulation",
                        }}
                      />
                      <input
                        type="text"
                        placeholder="CUIT a nombre de... (XX-XXXXXXXX-X)"
                        value={formData.factura_a_nombre_de}
                        onChange={(e) =>
                          handleCUITChange(
                            e.target.value,
                            "factura_a_nombre_de"
                          )
                        }
                        maxLength="13"
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          fontSize: "12px",
                          boxSizing: "border-box",
                          WebkitAppearance: "none",
                          appearance: "none",
                          touchAction: "manipulation",
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ✅ FOOTER UNIFICADO */}
        <div
          style={{
            background: "#f8fafc",
            padding: isMobile ? "15px 24px 15px 20px" : "20px 25px",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            gap: "12px",
            boxSizing: "border-box",
            width: "100%",
          }}
        >
          <button
            onClick={onClose}
            className="button-hover"
            style={{
              padding: isMobile ? "10px 16px" : "12px 20px",
              border: "2px solid #e2e8f0",
              borderRadius: "10px",
              background: "white",
              color: "#64748b",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <X size={16} />
            Cancelar
          </button>

          <button
            onClick={handleSave}
            disabled={guardando || subiendoArchivos}
            className="button-hover"
            style={{
              padding: isMobile ? "10px 20px" : "12px 24px",
              border: "none",
              borderRadius: "10px",
              background:
                guardando || subiendoArchivos ? "#94a3b8" : colorVista.gradient,
              color: "white",
              fontSize: "14px",
              fontWeight: "600",
              cursor: guardando || subiendoArchivos ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
            }}
          >
            {guardando ? (
              <>
                <Loader2 size={16} className="loading-pulse" />
                Guardando...
              </>
            ) : (
              <>
                <Save size={16} />
                Guardar Cobro
              </>
            )}
          </button>
        </div>
      </div>

    </div>
  );
}

export default Cobros;
